import React, { useState, useEffect } from 'react';
// DailyReport domain
import { saveDailyReportData, getDailyReportData, getFinancialTransactionData } from '../services/firebase/dailyReportFirebaseService.ts';
import { assessDailyShrink, summarizeReceipts, buildShrinkConfirmMessage, buildCollapsedBlockMessage, type ShrinkVerdict, type CollapsedDay } from '../utils/dailyReportShrinkGuard.ts';
// MonthlyData domain
import { getMonthlyReceiptData, getMonthlyExpenseData, getMonthlyVATData, getMonthlyFinancialTransactionData } from '../services/firebase/monthlyDataFirebaseService.ts';
// Budget domain
import { getBudgetData } from '../services/firebase/budgetFirebaseService.ts';
import { useAppContext } from '../contexts/AppContext.tsx';
import { useExcelProcessor } from '../features/daily-report/hooks/useExcelProcessor.ts';
import { useExcelExporter } from '../features/daily-report/hooks/useExcelExporter.ts';
import { ValidationError } from '../features/daily-report/types.ts';
import { db } from '../firebase';
import { doc, getDoc, setDoc, Timestamp, collection, getDocs } from 'firebase/firestore';
import { TeamCollectionService } from '../services/teamCollectionService.ts';
import { TeamPerformanceRefreshService } from '../services/teamPerformanceRefreshService.ts';
import BlankAssistAttributionPanel from '../components/daily-report/BlankAssistAttributionPanel.tsx';
import { useDailyReportOptimization } from '../hooks/useDailyReportOptimization.ts';
import DailyReportHeader from '../components/daily-report/DailyReportHeader.tsx';
import DataTable from '../components/common/DataTable.tsx';
import { dailyReportWebhookService } from '../services/dailyReportWebhookService.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

// 새로 분리된 컴포넌트들과 훅들
import Button from '../components/common/Button.tsx';
import Alert from '../components/common/Alert.tsx';
import ReceiptInput from '../components/daily-report/ReceiptInput.tsx';
import ExpenseInput from '../features/daily-report/components/ExpenseInput.tsx';
import { formatDate, getPreviousDay, getNextDay } from '../utils/dateUtils.ts';
import { DailyReportDebugLogger } from '../utils/debugUtils.ts';
import { accumulateSourceMax, SourceMaxMap } from '../utils/monthlyAggregation.ts';
import {
  ReceiptData,
  ExpenseData,
  AssistReceiptItem,
  ReferralReceiptItem,
  FinancialTransactionData,
  MonthlyTransactionSummary as MonthlyTransactionSummaryType
} from '../types/dailyReport.ts';
import {
  selectTargetEmployees,
  buildEmployeeTeamMap,
  getTeamForAssist
} from '../services/teamMonthlyDataService.ts';

// 디버그 함수들은 별도 유틸리티 파일에서 import됩니다.

// 🔧 디버그 모드 설정
const DEBUG_MODE = {
  EXCEL_UPLOAD: false,          // 엑셀 업로드 관련 로그
  FIREBASE_SAVE: false,         // Firebase 저장 관련 로그
  VAT_CALCULATION: false,       // 부가가치세 계산 관련 로그
  FILE_OPTIMIZATION: false,     // 파일 최적화 관련 로그
  COMPONENT_LIFECYCLE: false,   // 컴포넌트 생명주기 관련 로그
  ALL: false                    // 전체 디버그 로그 제어 (true 시 모든 로그 활성화)
};

// 🔧 디버그 로그 함수
const debugLog = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
  if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
    console.log(...args);
  }
};

const debugWarn = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
  if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
    console.warn(...args);
  }
};

const debugError = (category: keyof typeof DEBUG_MODE, ...args: any[]) => {
  if (DEBUG_MODE.ALL || DEBUG_MODE[category]) {
    console.error(...args);
  }
};



// 엑셀 정보 컴포넌트
const ExcelInfo = ({
  onFileUpload,
  onFileDownload,
  currentDate,
  hasData,
  isExporting
}: {
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileDownload: () => void;
  currentDate: string;
  hasData: boolean;
  isExporting: boolean;
}) => {
  const [fileInfo, setFileInfo] = useState<{
    fileName: string;
    fileUploadTime: string;
    uploadedBy: string;
    extractedDate: string;
  }>({
    fileName: '',
    fileUploadTime: '',
    uploadedBy: '관리자',
    extractedDate: ''
  });

  // 현재 날짜의 파일 정보를 Firebase에서 가져오기
  useEffect(() => {
    const loadFileInfo = async () => {
      try {
        const reportData = await getDailyReportData(currentDate);
        if (reportData && reportData.file) {
          const { name = '', uploadTime = '' } = reportData.file;
          
          // 파일에서 추출된 날짜 정보 확인
          let extractedDate = '';
          if (name) {
            const dateMatch = name.match(/(\d{4}-\d{2}-\d{2})진료비통계\.xlsx/);
            if (dateMatch && dateMatch[1]) {
              extractedDate = dateMatch[1];
            }
          }
          
          setFileInfo({
            fileName: name,
            fileUploadTime: uploadTime,
            uploadedBy: '관리자',
            extractedDate
          });
        } else {
          // 데이터가 없으면 초기화
          setFileInfo({
            fileName: '',
            fileUploadTime: '',
            uploadedBy: '관리자',
            extractedDate: ''
          });
        }
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '파일 정보 로드 실패:', error);
      }
    };

    loadFileInfo();
  }, [currentDate]);
  
  // 파일 업로드 핸들러 래핑 (업로드 성공 시 파일 정보 새로고침)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFileUpload(e);
    
    // 파일 업로드 후 잠시 후 파일 정보 새로고침
    setTimeout(() => {
      const loadFileInfo = async () => {
        try {
          const reportData = await getDailyReportData(currentDate);
          if (reportData && reportData.file) {
            const { name = '', uploadTime = '' } = reportData.file;
            
            // 파일에서 추출된 날짜 정보 확인
            let extractedDate = '';
            if (name) {
              const dateMatch = name.match(/(\d{4}-\d{2}-\d{2})진료비통계\.xlsx/);
              if (dateMatch && dateMatch[1]) {
                extractedDate = dateMatch[1];
              }
            }
            
            setFileInfo({
              fileName: name,
              fileUploadTime: uploadTime,
              uploadedBy: '관리자',
              extractedDate
            });
          }
        } catch (error) {
          DailyReportDebugLogger.error('ERROR_HANDLING', '파일 정보 새로고침 실패:', error);
        }
      };
      
      loadFileInfo();
    }, 1000); // 1초 후 새로고침
  };

  // 파일 업로드 상태 계산
  const hasFile = !!fileInfo.fileName;
  
  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h2 className="text-lg font-semibold mb-4">엑셀 정보</h2>
          <div className="flex mb-4">
            <Button type="primary" className="mr-2">
              엑셀형
            </Button>
            <Button type="secondary">
              원본형
            </Button>
          </div>
          
          <div className="border border-gray-300 rounded p-4 bg-gray-50">
            <p className="mb-2 font-medium text-gray-700">엑셀 파일 관리</p>
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 mr-2 overflow-hidden">
                <input
                  type="file"
                  id="fileUpload"
                  className="hidden"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                />
                <label
                  htmlFor="fileUpload"
                  className="cursor-pointer px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded inline-block mr-2"
                >
                  📁 파일 업로드
                </label>
                <button
                  onClick={onFileDownload}
                  disabled={!hasData || isExporting}
                  className={`px-4 py-2 rounded inline-block ${
                    hasData && !isExporting
                      ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isExporting ? '📥 내보내는 중...' : '📥 엑셀 다운로드'}
                </button>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">현재 파일:</span> {fileInfo.fileName || '업로드된 파일 없음'}
            </div>
          </div>
        </div>
        
        <div>
          <h2 className="text-lg font-semibold mb-4">엑셀 정보 결과 확인</h2>
          <div className="border border-gray-300 rounded p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm text-gray-700 font-medium">파일 업로드 일시</div>
              <div className="text-sm">{fileInfo.fileUploadTime || '-'}</div>
              
              <div className="text-sm text-gray-700 font-medium">파일명</div>
              <div className="text-sm overflow-hidden truncate">{fileInfo.fileName || '-'}</div>
              
              <div className="text-sm text-gray-700 font-medium">데이터 날짜</div>
              <div className="text-sm">{fileInfo.extractedDate || currentDate}</div>
              
              <div className="text-sm text-gray-700 font-medium">등록자ID</div>
              <div className="text-sm">{fileInfo.uploadedBy}</div>
              
              <div className="text-sm text-gray-700 font-medium">상태</div>
              <div className="text-sm">
                {hasFile ? (
                  <span className="text-green-600 font-medium">
                    ✓ 업로드 완료
                  </span>
                ) : (
                  <span className="text-gray-500">
                    대기 중
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 일계표 현황 컴포넌트
const ReportStatus = ({ 
  totalReceipt, 
  onSave,
  currentDate,
  prevDayTotal,
  monthlyTotal,
  monthlyVAT,
  dailyCashReceipt,
  dailyCardReceipt,
  dailyTransferReceipt,
  dailyUncollectedReceipt,
  dailyInsuranceClaim,
  dailyHealthLifeSupport,
  dailyExpenseTotal
}: {
  totalReceipt: number;
  onSave: () => void;
  currentDate: string;
  prevDayTotal: number;
  monthlyTotal: number;
  monthlyVAT: number;
  dailyCashReceipt: number;
  dailyCardReceipt: number;
  dailyTransferReceipt: number;
  dailyUncollectedReceipt: number;
  dailyInsuranceClaim: number;
  dailyHealthLifeSupport: number;
  dailyExpenseTotal: number;
}) => {
  
  // 🎯 총수납 = 현금 + 계좌이체 + 카드 + 청구액 + 건강생활유지비 (미수금 제외)
  const totalDailyReceipt = dailyCashReceipt + dailyTransferReceipt + dailyCardReceipt + 
           // dailyUncollectedReceipt +  // 🎯 미수금 제외 (실제 현금 흐름 아님)
           dailyInsuranceClaim + dailyHealthLifeSupport;
  
  // 디버깅 로그 추가
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `🧮 [ReportStatus VAT 디버깅] ${currentDate} 일계표 현황 데이터:`, {
    currentDate,
    totalDailyReceipt: totalDailyReceipt.toLocaleString(),
    prevDayTotal: prevDayTotal.toLocaleString(),
    monthlyTotal: monthlyTotal.toLocaleString(),
    monthlyVAT: monthlyVAT.toLocaleString(),
    dailyExpenseTotal: dailyExpenseTotal.toLocaleString()
  });

  const data = [
    {
      '구분': '일계표현황',
      '총수납': totalDailyReceipt,
      '전일총수납액': prevDayTotal,
      '월총매출액 (순수익)': monthlyTotal,
      '월총부가가치세': monthlyVAT,
      '지출합계': dailyExpenseTotal === 0 ? 0 : -dailyExpenseTotal,
      '메모': `${currentDate} 일계표`
    }
  ];

  // const renderCell = (row: any, column: string) => {
  //   if (['총수납', '전일총수납액', '월총매출액 (순수익)', '지출합계'].includes(column) && typeof row[column] === 'number') {
  //     return row[column].toLocaleString() + '원';
  //   }
  //   return row[column];
  // }; // DataTable로 교체되어 사용하지 않음
  
  return (
    <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">데일리 리포트 현황</h2>
        <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full border border-green-200">
          📊 {currentDate}
        </div>
      </div>
      
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
            <DataTable 
              data={data}
              columns={[
                { key: '구분', header: '구분', sortable: false, width: 'w-20 sm:w-24' },
                { key: '총수납', header: '총수납', align: 'left', sortable: false, render: (value) => typeof value === 'number' ? value.toLocaleString() + '원' : value, width: 'w-24 sm:w-28' },
                { key: '전일총수납액', header: '전일총수납액', align: 'left', sortable: false, render: (value) => typeof value === 'number' ? value.toLocaleString() + '원' : value, width: 'w-28 sm:w-32' },
                { key: '월총매출액 (순수익)', header: '월총매출액 (순수익)', align: 'left', sortable: false, render: (value) => typeof value === 'number' ? value.toLocaleString() + '원' : value, width: 'w-32 sm:w-36' },
                { key: '월총부가가치세', header: '월총부가가치세', align: 'left', sortable: false, render: (value) => typeof value === 'number' ? value.toLocaleString() + '원' : value, width: 'w-28 sm:w-32' },
                { key: '지출합계', header: '지출합계', align: 'left', sortable: false, render: (value) => typeof value === 'number' ? value.toLocaleString() + '원' : value, width: 'w-24 sm:w-28' },
                { key: '메모', header: '메모', sortable: false, width: 'w-32 sm:w-36' }
              ]}
              keyField="구분"
              sortable={false}
              showFooter={false}
            />
          </div>
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-3 sm:justify-end mt-6 pt-4 border-t border-gray-100">
        <div className="flex flex-col items-end gap-2">
          <Button type="primary" onClick={() => {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '📊 [월별집계] 수동 재계산 버튼 클릭됨!');
            onSave();
          }} className="w-full sm:w-auto">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            월별 집계 재계산
          </Button>
          <p className="text-xs text-gray-500 text-right">
            💡 이미 저장된 데이터를 기반으로 월별 팀 집계를 재계산합니다
            <br />
            <span className="text-gray-400">
              • 어시스트별 월별 수납액, 지출액, 순수익 업데이트
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

// TeamCollectionReport 컴포넌트가 제거되었습니다

// 🆕 팀별 월 수납 현황 컴포넌트
const MonthlyTeamCollectionReport = ({
  currentDate
}: {
  currentDate: string;
}) => {
  const { budgetData, employees } = useAppContext(); // ✅ budgetData와 employees 가져오기

  const [monthlyData, setMonthlyData] = React.useState<Array<{teamName: string; monthlyReceipt: number; monthlyExpense: number; monthlyProfit: number}>>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [forceRefreshKey, setForceRefreshKey] = React.useState<number>(0); // 강제 리마운트용

  const currentYearMonth = currentDate.substring(0, 7); // YYYY-MM 형식

  // 🔄 강제 캐시 클리어 및 리프레시 함수
  const forceClearCacheAndRefresh = React.useCallback(() => {
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀별 월 수납] 🔄 강제 캐시 클리어 및 리프레시 시작');

    // localStorage 클리어 (팀별 관련 캐시)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('team') || key.includes('monthly') || key.includes('collection'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀별 월 수납] localStorage 캐시 클리어 완료:', keysToRemove.length, '개 항목');
    } catch (error) {
      DailyReportDebugLogger.warn('MONTHLY_CALCULATION', '[팀별 월 수납] localStorage 클리어 실패:', error);
    }

    // sessionStorage 클리어
    try {
      sessionStorage.clear();
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀별 월 수납] sessionStorage 캐시 클리어 완료');
    } catch (error) {
      DailyReportDebugLogger.warn('MONTHLY_CALCULATION', '[팀별 월 수납] sessionStorage 클리어 실패:', error);
    }

    // 컴포넌트 상태 초기화 및 강제 리마운트
    setForceRefreshKey(prev => prev + 1);
    setMonthlyData([]);
    setError(null);

    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[팀별 월 수납] 🔄 강제 리프레시 완료, 새로운 키:', forceRefreshKey + 1);
  }, [forceRefreshKey]);

  // 팀명 정규화 함수 (월별 표시용만, 이 컴포넌트에서만 사용)
  const normalizeTeamNameForDisplay = (teamName: string): string => {
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] 🔍 입력: "${teamName}"`);

    if (!teamName || teamName.trim() === '') {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] ❌ 빈 입력값, '미지정' 반환`);
      return '미지정'; // 빈 문자열 대신 '미지정' 반환
    }

    const trimmedName = teamName.trim();

    // '이름-미지정' 형태 처리
    if (trimmedName.includes('-미지정') || trimmedName === '미지정') {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] ⚠️ 미지정 팀: "${trimmedName}" → "미지정"`);
      return '미지정';
    }

    // '이름-숫자팀' 형태를 '숫자팀'으로 변환 (예: '박가영-2팀' → '2팀')
    const hyphenTeamMatch = trimmedName.match(/^.*-(\d+팀)$/);
    if (hyphenTeamMatch) {
      const result = hyphenTeamMatch[1];
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] ✅ 하이픈 패턴 매칭: "${trimmedName}" → "${result}"`);
      return result;
    }

    // '이름숫자팀' 형태를 '숫자팀'으로 변환 (하이픈 없는 경우)
    const directTeamMatch = trimmedName.match(/^.*?(\d+팀)$/);
    if (directTeamMatch && trimmedName !== directTeamMatch[1]) {
      const result = directTeamMatch[1];
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] ✅ 직접 패턴 매칭: "${trimmedName}" → "${result}"`);
      return result;
    }

    // 패턴이 매칭되지 않으면
    if (trimmedName.includes('-')) {
      // 하이픈이 있지만 팀 패턴이 아닌 경우 (예: '박가영-기타')
      const parts = trimmedName.split('-');
      const teamPart = parts[parts.length - 1]; // 마지막 부분을 팀명으로 사용
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] ⚠️ 하이픈 분리: "${trimmedName}" → "${teamPart}"`);
      return teamPart || '미지정';
    }

    DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀명 정규화] ⚠️ 패턴 미매칭, 원본 반환: "${trimmedName}"`);
    return trimmedName || '미지정'; // 매칭되지 않으면 원본 반환, 그마저도 없으면 '미지정'
  };

  React.useEffect(() => {
    // ✅ 취소 가드: employees·budgetData identity 변경으로 재조회가 중첩될 때
    //    늦게 resolve된 stale 요청이 최신 결과를 덮어쓰지 않도록 커밋 전 확인
    let cancelled = false;

    const loadMonthlyTeamData = async () => {
      try {
        DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
        setError(null);
        
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] ${currentYearMonth} 데이터 로드 시작`);
        
        // Firebase에서 해당 월의 팀별 월 수납 데이터 가져오기
        // Firebase imports moved to top
        
        const reportsCol = collection(db, 'dailyReports');
        const snapshot = await getDocs(reportsCol);
        if (cancelled) return; // stale 요청: 이후 집계·커밋 생략

                 // compositeKey별 월 수납액, 지출액, 순수익 집계
         // ✅ sourceMax: 출처(원본 compositeKey)별 MAX 맵 — 같은 출처는 MAX, 다른 출처는 SUM(총합 = Σ 출처별 MAX)
         const compositeKeyData = new Map<string, {compositeKey: string; sourceMax: SourceMaxMap; monthlyReceipt: number; monthlyExpense: number; monthlyProfit: number}>();

         // ✅ 공통 서비스: 월별 저장 직원 데이터 우선 사용 (ActualSalesService와 동일)
         const targetEmployees = selectTargetEmployees(employees, budgetData, currentYearMonth);
         const employeeTeamMap = buildEmployeeTeamMap(targetEmployees);

         DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] ${currentYearMonth} 직원 데이터 (${targetEmployees.length}명), 팀 매핑 (${Object.keys(employeeTeamMap).length}개)`);

         // 🆕 TeamCollectionService 인스턴스 생성 (3순위 팀 매핑용)
         const teamCollectionService = new TeamCollectionService();

         // 🔄 2단계 처리: 1) teamCollection 먼저, 2) Financial Transaction 나중에
         DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] 🔄 1단계: teamCollection 데이터 처리 시작`);

         for (const doc of snapshot.docs) {
           if (cancelled) return; // stale 요청: 문서별 매핑(await) 반복 조기 중단
           const docDate = doc.id;

           // 해당 월의 날짜인지 확인
           if (docDate.startsWith(currentYearMonth)) {
             const data = doc.data();

             // 🔍 데이터 구조 분석 로그 추가
             DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] ${docDate} 데이터 구조 분석:`, {
               hasTeamCollection: !!data.teamCollection,
               teamCollectionType: Array.isArray(data.teamCollection) ? 'array' : typeof data.teamCollection,
               teamCollectionLength: Array.isArray(data.teamCollection) ? data.teamCollection.length : 0,
               hasFinancialTransactions: !!data.financialTransactions,
               financialTransactionsType: typeof data.financialTransactions,
               hasFinancialTransactionItems: !!(data.financialTransactions?.items),
               financialTransactionItemsLength: Array.isArray(data.financialTransactions?.items) ? data.financialTransactions.items.length : 0,
               dataKeys: Object.keys(data)
             });

             // financialTransactions 상세 구조 로그
             if (data.financialTransactions) {
               DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] ${docDate} Financial Transactions 구조:`, {
                 structure: data.financialTransactions,
                 hasItems: !!(data.financialTransactions.items),
                 itemsType: typeof data.financialTransactions.items,
                 firstItem: Array.isArray(data.financialTransactions.items) && data.financialTransactions.items.length > 0 ? data.financialTransactions.items[0] : null
               });
             }

             // teamCollection에서 월별 집계 데이터 수집 (ActualSalesService 패턴)
             if (data.teamCollection && Array.isArray(data.teamCollection)) {
               for (const item of data.teamCollection as any[]) {
                 if (item.isAssistRow && item.compositeKey && typeof item.monthlyReceipt === 'number') {
                   // ✅ compositeKey에서 어시스트 이름 추출
                   const [originalAssistName] = item.compositeKey.split('-');
                   const assistName = originalAssistName || item.assistName || '미지정';

                   // ✅ 공통 서비스: 5단계 팀 매핑 폴백 (ActualSalesService와 동일)
                   const isFinancialTransaction = item.isFinancialTransaction || false;
                   let currentTeam = getTeamForAssist(
                     assistName,
                     employeeTeamMap,
                     item.cashierName,
                     isFinancialTransaction ? item.assistTeam : undefined,
                     isFinancialTransaction ? item.teamName : undefined,
                     item.team
                   );

                   // 🆕 3순위: TeamCollectionService (ActualSalesService와 동일)
                   if (currentTeam === '미지정' && targetEmployees.length > 0) {
                     try {
                       const mappedTeam = await teamCollectionService.mapItemToTeamPublic(item, targetEmployees, undefined, budgetData, currentYearMonth);
                       if (mappedTeam && mappedTeam !== '미지정') {
                         currentTeam = mappedTeam;
                         DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] 🎯 TeamCollectionService 매핑: ${assistName} → ${currentTeam}`);
                       }
                     } catch (serviceError) {
                       DailyReportDebugLogger.warn('MONTHLY_CALCULATION', `[팀별 월 수납] ⚠️ TeamCollectionService 매핑 오류: ${assistName}`, serviceError);
                     }
                   }

                   // ✅ 현재 팀명으로 새로운 compositeKey 생성
                   const currentCompositeKey = `${assistName}-${currentTeam}`;

                   const monthlyReceipt = item.monthlyReceipt || 0;
                   const monthlyExpense = item.monthlyExpense || 0;

                   if (compositeKeyData.has(currentCompositeKey)) {
                     const existing = compositeKeyData.get(currentCompositeKey)!;

                     if (isFinancialTransaction) {
                       // Financial: 날짜별 개별 거래이므로 항상 합산 (+=)
                       existing.monthlyReceipt += monthlyReceipt;
                       existing.monthlyExpense += monthlyExpense;
                     } else {
                       // ✅ 같은 출처(원본 compositeKey)는 MAX, 다른 출처는 SUM — 출처별 MAX 맵으로
                       //    총합 = Financial(+=)분 + Σ(출처별 MAX) 보장
                       //    ('직전 키 1개 기억'은 키가 A→B→A로 교차 도착 시 SUM 재발화로 이중계상)
                       const delta = accumulateSourceMax(existing.sourceMax, item.compositeKey || '', monthlyReceipt, monthlyExpense);
                       existing.monthlyReceipt += delta.receipts;
                       existing.monthlyExpense += delta.expenses;
                     }
                     existing.monthlyProfit = existing.monthlyReceipt - existing.monthlyExpense;
                   } else {
                     // 새로운 compositeKey 추가 (비Financial은 출처별 MAX 맵에 기준값 등록)
                     const sourceMax: SourceMaxMap = {};
                     if (!isFinancialTransaction) {
                       accumulateSourceMax(sourceMax, item.compositeKey || '', monthlyReceipt, monthlyExpense);
                     }
                     compositeKeyData.set(currentCompositeKey, {
                       compositeKey: currentCompositeKey,
                       sourceMax,
                       monthlyReceipt: monthlyReceipt,
                       monthlyExpense: monthlyExpense,
                       monthlyProfit: monthlyReceipt - monthlyExpense
                     });
                   }
                 }
               }
               DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] ${docDate} teamCollection 처리 완료:`, {
                 teamCollectionItems: data.teamCollection.length,
                 assistRowCount: data.teamCollection.filter((item: { isAssistRow?: boolean }) => item.isAssistRow).length,
                 compositeKeyCount: compositeKeyData.size
               });
             } else {
               DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] ${docDate} teamCollection 없음 - financial transactions만으로 구성`, {
                 hasTeamCollection: !!data.teamCollection,
                 teamCollectionType: typeof data.teamCollection
               });
             }
             
             // financialTransactions 데이터도 집계에 포함
             if (data.financialTransactions && data.financialTransactions.items) {
               const incomeTransactions = data.financialTransactions.items.filter((t: FinancialTransactionItem) => t.transactionType === 'income');
               const expenseTransactions = data.financialTransactions.items.filter((t: FinancialTransactionItem) => t.transactionType === 'expense');

               DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] Financial Transactions 처리: 수입 ${incomeTransactions.length}건, 지출 ${expenseTransactions.length}건`);

               for (const transaction of data.financialTransactions.items as FinancialTransactionItem[]) {
                 const assistName = transaction.assistName || '미지정';

                 // ✅ 공통 서비스: 5단계 팀 매핑 폴백 (ActualSalesService와 동일)
                 let teamName = getTeamForAssist(
                   assistName,
                   employeeTeamMap,
                   transaction.cashierName,
                   transaction.assistTeam,
                   transaction.teamName,
                   transaction.team
                 );

                 // 🆕 3순위: TeamCollectionService (ActualSalesService와 동일)
                 if (teamName === '미지정' && targetEmployees.length > 0) {
                   try {
                     const mappedTeam = await teamCollectionService.mapItemToTeamPublic(
                       transaction as any,
                       targetEmployees,
                       undefined,
                       budgetData,
                       currentYearMonth
                     );
                     if (mappedTeam && mappedTeam !== '미지정') {
                       teamName = mappedTeam;
                       DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] 🎯 Financial TeamCollectionService 매핑: ${assistName} → ${teamName}`);
                     }
                   } catch (serviceError) {
                     DailyReportDebugLogger.warn('MONTHLY_CALCULATION', `[팀별 월 수납] ⚠️ Financial TeamCollectionService 매핑 오류: ${assistName}`, serviceError);
                   }
                 }

                 // ✅ Financial 거래는 '-financial' 접미사로 버킷 분리 (정본 ActualSalesService와 동일)
                 //    teamCollection 누적값과 키 공간을 공유하면 MAX 기준선이 오염되므로 별도 버킷에 합산 후 팀 합산 단계에서 합류
                 const compositeKey = `${assistName}-${teamName}-financial`;

                 // Financial Transaction 처리 (상세 로그 제거)

                 if (!compositeKeyData.has(compositeKey)) {
                   compositeKeyData.set(compositeKey, {
                     compositeKey: compositeKey,
                     sourceMax: {}, // Financial Transaction은 += 합산이므로 출처별 MAX 맵 미사용
                     monthlyReceipt: 0,
                     monthlyExpense: 0,
                     monthlyProfit: 0
                   });
                 }

                 const existing = compositeKeyData.get(compositeKey)!;
                 const amount = transaction.amount || 0;

                 if (transaction.transactionType === 'expense') {
                   existing.monthlyExpense += amount;
                   existing.monthlyProfit = existing.monthlyReceipt - existing.monthlyExpense;
                 } else if (transaction.transactionType === 'income') {
                   existing.monthlyReceipt += amount;
                   existing.monthlyProfit = existing.monthlyReceipt - existing.monthlyExpense;
                 }
               }

               DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] Financial Transaction 처리 완료: ${compositeKeyData.size}개 키`);
             } else {
               DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] Financial Transactions 없음:`, {
                 date: data.date,
                 hasTransactions: !!data.financialTransactions,
                 hasItems: !!(data.financialTransactions?.items)
               });
             }
           }
         }

         // 🔍 최종 compositeKeyData 상태 로그
         DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[팀별 월 수납] 최종 compositeKeyData 구성 상태:`, {
           totalCompositeKeys: compositeKeyData.size,
           compositeKeys: Array.from(compositeKeyData.keys()),
           dataDetails: Array.from(compositeKeyData.values()).map(item => ({
             compositeKey: item.compositeKey,
             monthlyReceipt: item.monthlyReceipt,
             monthlyExpense: item.monthlyExpense,
             monthlyProfit: item.monthlyProfit
           })),
           hasAnyFinancialData: Array.from(compositeKeyData.values()).some(item =>
             item.monthlyReceipt !== 0 || item.monthlyExpense !== 0
           )
         });

         // 정규화 전 총합 계산
         const beforeNormalizationTotal = Array.from(compositeKeyData.values()).reduce((sum, item) => sum + item.monthlyReceipt, 0);

         // 정규화된 팀명으로 그룹화하여 합산
         const normalizedTeamData = new Map<string, {normalizedTeamName: string; monthlyReceipt: number; monthlyExpense: number; monthlyProfit: number}>();

         Array.from(compositeKeyData.values()).forEach((item) => {
           // ✅ financial 버킷은 팀 합산 단계에서 접미사 제거 후 원래 팀으로 합류
           const teamKeySource = item.compositeKey.endsWith('-financial')
             ? item.compositeKey.slice(0, -'-financial'.length)
             : item.compositeKey;
           const normalizedTeamName = normalizeTeamNameForDisplay(teamKeySource);

           if (normalizedTeamData.has(normalizedTeamName)) {
             // 이미 존재하는 정규화된 팀명이면 금액 합산
             const existing = normalizedTeamData.get(normalizedTeamName)!;
             existing.monthlyReceipt += item.monthlyReceipt;
             existing.monthlyExpense += item.monthlyExpense;
             existing.monthlyProfit += item.monthlyProfit;
           } else {
             // 새로운 정규화된 팀명이면 추가
             normalizedTeamData.set(normalizedTeamName, {
               normalizedTeamName,
               monthlyReceipt: item.monthlyReceipt,
               monthlyExpense: item.monthlyExpense,
               monthlyProfit: item.monthlyProfit
             });
           }
         });

         // 정규화 후 총합 계산
         const afterNormalizationTotal = Array.from(normalizedTeamData.values()).reduce((sum, item) => sum + item.monthlyReceipt, 0);

          // 데이터 무결성 검증
          const integrityCheck = {
            정규화전_총합: beforeNormalizationTotal,
            정규화후_총합: afterNormalizationTotal,
            차이: beforeNormalizationTotal - afterNormalizationTotal,
            무결성: beforeNormalizationTotal === afterNormalizationTotal
          };

          if (!integrityCheck.무결성) {
            console.error('🚨 데이터 무결성 오류 발견!', {
              정규화전: integrityCheck.정규화전_총합.toLocaleString() + '원',
              정규화후: integrityCheck.정규화후_총합.toLocaleString() + '원',
              누락금액: integrityCheck.차이.toLocaleString() + '원'
            });
          }

         DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[데이터 집계] ✅ 집계 완료, 정규화된 팀 수: ${normalizedTeamData.size}개`);

         // 데이터 집계 완료

         // Map을 배열로 변환하고 정렬
         const teamDataArray = Array.from(normalizedTeamData.values())
           .map(item => ({
             teamName: item.normalizedTeamName, // 정규화된 팀명 표시
             monthlyReceipt: item.monthlyReceipt,
             monthlyExpense: item.monthlyExpense,
             monthlyProfit: item.monthlyProfit
           }))
           .sort((a, b) => a.teamName.localeCompare(b.teamName));

        if (cancelled) return; // stale 요청: 최신 요청 결과만 커밋
        setMonthlyData(teamDataArray);

      } catch (err) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '[팀별 월 수납] 데이터 로드 실패:', err);
        if (!cancelled) {
          setError('월별 수납 데이터를 불러오는데 실패했습니다.');
        }
      } finally {
        if (!cancelled) {
          DailyReportDebugLogger.loadingState('END', 'Firebase 저장 완료 후');
          setIsLoading(false);
        }
      }
    };

    loadMonthlyTeamData();
    // ✅ cleanup: deps 변경/언마운트 시 진행 중 요청 취소 표시 → stale 커밋 차단
    return () => { cancelled = true; };
    // ✅ employees·budgetData 추가: 월별 직원/의사매핑(doctorTeamMapping) 인자가 mount 스냅샷에 고정되지 않도록 (MonthlyAssistantReport와 동일)
  }, [currentYearMonth, forceRefreshKey, employees, budgetData]);

  if (isLoading) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">팀별 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-700 text-sm font-medium rounded-full border border-purple-200">
            📊 {currentYearMonth}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">월별 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">팀별 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-700 text-sm font-medium rounded-full border border-purple-200">
            📊 {currentYearMonth}
          </div>
        </div>
        <Alert type="error" title="오류">
          {error}
        </Alert>
      </div>
    );
  }

  const totalReceipt = monthlyData.reduce((sum, item) => sum + item.monthlyReceipt, 0);
  const totalExpense = monthlyData.reduce((sum, item) => sum + item.monthlyExpense, 0);
  const totalProfit = monthlyData.reduce((sum, item) => sum + item.monthlyProfit, 0);

  const tableData = monthlyData.map((item, index) => ({
    '팀': item.teamName,
    '월 수납액': item.monthlyReceipt,
    '월 지출액': item.monthlyExpense,
    '월순매출(순수익)': item.monthlyProfit,
    uniqueKey: `monthly-${index}`
  }));

  const footerData = {
    '팀': '합계',
    '월 수납액': totalReceipt.toLocaleString(),
    '월 지출액': totalExpense.toLocaleString(),
    '월순매출(순수익)': totalProfit.toLocaleString()
  };

  return (
    <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">팀별 월 수납 현황</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-700 text-sm font-medium rounded-full border border-purple-200">
            📊 {currentYearMonth}
          </div>
          <button
            onClick={forceClearCacheAndRefresh}
            className="inline-flex items-center px-3 py-1 bg-red-50 text-red-700 text-sm font-medium rounded-full border border-red-200 hover:bg-red-100 transition-colors"
            title="캐시 클리어 및 강제 새로고침"
          >
            🔄 새로고침
          </button>
        </div>
      </div>
      
      {monthlyData.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">월별 데이터가 없습니다</h3>
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
            <span className="font-medium text-gray-700">{currentYearMonth}</span>월에 집계된 팀별 수납 데이터가 없습니다.
            <br className="hidden sm:block" />
            데일리 리포트 데이터를 먼저 입력해주세요.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                <DataTable 
                  data={tableData}
                  columns={[
                    { key: '팀', header: '팀', sortable: true, width: 'w-32 sm:w-40' },
                    { key: '월 수납액', header: '월 수납액', align: 'right', sortable: true, width: 'w-28 sm:w-32', render: (value) => <span className="font-mono text-sm">{typeof value === 'number' ? value.toLocaleString() + '원' : value}</span> },
                    { key: '월 지출액', header: '월 지출액', align: 'right', sortable: true, width: 'w-28 sm:w-32', render: (value) => <span className="font-mono text-sm">{typeof value === 'number' ? value.toLocaleString() + '원' : value}</span> },
                    { key: '월순매출(순수익)', header: '월순매출(순수익)', align: 'right', sortable: true, width: 'w-32 sm:w-36', render: (value) => <span className="font-mono text-sm font-medium">{typeof value === 'number' ? value.toLocaleString() + '원' : value}</span> }
                  ]}
                  keyField="uniqueKey"
                  showFooter={true}
                  footerData={footerData}
                  sortable={true}
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 sm:p-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm sm:text-base text-purple-700 font-medium">
                총 {monthlyData.length}개 팀의 집계가 완료되었습니다.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm sm:text-base">
              <div className="bg-white p-3 rounded-lg border border-purple-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">총 월 수납액</div>
                <div className="font-mono font-bold text-purple-700">{totalReceipt.toLocaleString()}원</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-purple-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">총 월 지출액</div>
                <div className="font-mono font-bold text-red-600">{totalExpense.toLocaleString()}원</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-purple-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">총 월 순수익</div>
                <div className="font-mono font-bold text-green-600">{totalProfit.toLocaleString()}원</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
// 🆕 어시스트별 월 수납 현황 컴포넌트 (assistReceipts 데이터 사용)
const MonthlyAssistantReport = ({
  currentDate
}: {
  currentDate: string;
}) => {
  const { budgetData, employees } = useAppContext(); // ✅ budgetData와 employees 가져오기

  const [monthlyData, setMonthlyData] = React.useState<Array<{
    assistName: string;
    teamName: string;
    monthlyReceipt: number;
    monthlyCount: number;
    dailyReceipt: number;
    dailyCount: number;
  }>>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [forceRefreshKey, setForceRefreshKey] = React.useState<number>(0); // 강제 리마운트용

  const currentYearMonth = currentDate.substring(0, 7); // YYYY-MM 형식
  const currentDateFormatted = formatDate(new Date(currentDate));

  // 🔄 강제 캐시 클리어 및 리프레시 함수
  const forceClearCacheAndRefresh = React.useCallback(() => {
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[어시스트별 월 수납] 🔄 강제 캐시 클리어 및 리프레시 시작');

    // localStorage 클리어 (어시스트 관련 캐시)
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('assist') || key.includes('monthly') || key.includes('collection'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[어시스트별 월 수납] localStorage 캐시 클리어 완료:', keysToRemove.length, '개 항목');
    } catch (error) {
      DailyReportDebugLogger.warn('MONTHLY_CALCULATION', '[어시스트별 월 수납] localStorage 클리어 실패:', error);
    }

    // sessionStorage 클리어
    try {
      sessionStorage.clear();
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[어시스트별 월 수납] sessionStorage 캐시 클리어 완료');
    } catch (error) {
      DailyReportDebugLogger.warn('MONTHLY_CALCULATION', '[어시스트별 월 수납] sessionStorage 클리어 실패:', error);
    }

    // 컴포넌트 상태 초기화 및 강제 리마운트
    setForceRefreshKey(prev => prev + 1);
    setMonthlyData([]);
    setError(null);

    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[어시스트별 월 수납] 🔄 강제 리프레시 완료, 새로운 키:', forceRefreshKey + 1);
  }, [forceRefreshKey]);

  React.useEffect(() => {
    const loadMonthlyAssistData = async () => {
      try {
        DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
        setError(null);

        // ✅ employees 로드 완료 전에는 Firebase 쿼리 스킵 (팀 매핑 불가)
        if (employees.length === 0) {
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[어시스트별 월 수납] ⏳ 직원 데이터 로드 대기 중...');
          return;
        }

        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `🔍 [어시스트별 월 수납] ${currentYearMonth} 데이터 로드 시작`);
        
        // Firebase에서 해당 월의 모든 assistReceipts 데이터 가져오기
        // Firebase imports moved to top
        
        const reportsCol = collection(db, 'dailyReports');
        const snapshot = await getDocs(reportsCol);
        
        // ✅ 월별 저장 직원 데이터 우선 사용 (급여 관리와 동일 패턴)
        const monthlyBudgetData = budgetData.monthlyData?.[currentYearMonth];
        const monthlyEmployees = monthlyBudgetData?.employees;
        const targetEmployees = (monthlyEmployees && monthlyEmployees.length > 0)
          ? monthlyEmployees
          : employees;

        if (monthlyEmployees && monthlyEmployees.length > 0) {
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[어시스트별 월 수납] ${currentYearMonth} 월별 저장 직원 데이터 사용 (${targetEmployees.length}명)`);
        } else {
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[어시스트별 월 수납] ${currentYearMonth} 전역 직원 데이터 사용 (${targetEmployees.length}명)`);
        }

        // 직원 팀 매핑 생성
        const employeeTeamMap: Record<string, string> = {};
        targetEmployees.forEach(emp => {
          employeeTeamMap[emp.name] = emp.team || '미지정';
        });
        
        const assistMonthlyData: Record<string, {
          assistName: string;
          teamName: string;
          monthlyReceipt: number;
          monthlyCount: number;
          dailyReceipt: number;
          dailyCount: number;
        }> = {};

        let processedDocs = 0;

        // 디버깅: 원본 데이터 수집 및 분석
        const DEBUG_MODE = localStorage.getItem('DEBUG_DAILY_REPORT') === 'true';
        const allAssistReceipts: any[] = [];
        const allReferralReceipts: any[] = [];

        snapshot.docs.forEach(doc => {
          const docDate = doc.id; // YYYY-MM-DD

          if (docDate.startsWith(currentYearMonth)) {
            processedDocs++;
            const data = doc.data();

            // 디버깅: 원본 assistReceipts 수집
            if (DEBUG_MODE && data && data.assistReceipts) {
              allAssistReceipts.push(...data.assistReceipts);
            }
            if (DEBUG_MODE && data && data.referralReceipts) {
              allReferralReceipts.push(...data.referralReceipts);
            }
            
            // assistReceipts에서 어시스트별 데이터 집계
            if (data && data.assistReceipts && Array.isArray(data.assistReceipts)) {
              data.assistReceipts.forEach((receipt: AssistReceiptItem) => {
                const assistName = receipt.assistName || '미지정';
                const amount = receipt.amount || 0;
                const count = receipt.count || 1;
                const teamName = employeeTeamMap[assistName] || '미지정';

                // 복합키 처리 (compositeKey가 있는 경우) - 원본 팀명 사용
                let finalAssistName = assistName;
                let finalTeamName = teamName; // 기본값은 employeeTeamMap에서 가져온 팀명

                if (receipt.compositeKey) {
                  const [originalAssistName, originalTeamName] = receipt.compositeKey.split('-');
                  finalAssistName = originalAssistName || assistName;
                  finalTeamName = originalTeamName || teamName; // compositeKey의 원래 팀명 사용
                }

                const key = `${finalAssistName}-${finalTeamName}`;

                if (!assistMonthlyData[key]) {
                  assistMonthlyData[key] = {
                    assistName: finalAssistName,
                    teamName: finalTeamName,
                    monthlyReceipt: 0,
                    monthlyCount: 0,
                    dailyReceipt: 0,
                    dailyCount: 0
                  };
                }

                assistMonthlyData[key].monthlyReceipt += amount;
                assistMonthlyData[key].monthlyCount += count;
                
                // 당일 데이터인 경우 별도 집계
                if (docDate === currentDateFormatted) {
                  assistMonthlyData[key].dailyReceipt += amount;
                  assistMonthlyData[key].dailyCount += count;
                }
              });
            }
            
            // financialTransactions 데이터도 집계에 포함
            if (data && data.financialTransactions && data.financialTransactions.items) {
              const transactionCount = data.financialTransactions.items.length;
              const incomeTransactions = data.financialTransactions.items.filter((t: FinancialTransactionItem) => t.transactionType === 'income');
              const expenseTransactions = data.financialTransactions.items.filter((t: FinancialTransactionItem) => t.transactionType === 'expense');

              DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[어시스트별 월 수납] ${docDate} Financial Transactions 발견:`, {
                totalCount: transactionCount,
                incomeCount: incomeTransactions.length,
                expenseCount: expenseTransactions.length,
                totalIncomeAmount: incomeTransactions.reduce((sum: number, t: FinancialTransactionItem) => sum + (t.amount || 0), 0),
                totalExpenseAmount: expenseTransactions.reduce((sum: number, t: FinancialTransactionItem) => sum + (t.amount || 0), 0),
                firstTransaction: data.financialTransactions.items[0]
              });

              data.financialTransactions.items.forEach((transaction: FinancialTransactionItem) => {
                const assistName = transaction.assistName || '미지정';
                const teamName = transaction.assistTeam || employeeTeamMap[assistName] || '미지정';
                const amount = transaction.amount || 0;
                const count = 1;
                
                // 거래 유형에 따라 금액 조정 (지출은 마이너스)
                const adjustedAmount = transaction.transactionType === 'expense' ? -amount : amount;
                
                const key = `${assistName}-${teamName}`;
                
                if (!assistMonthlyData[key]) {
                  assistMonthlyData[key] = {
                    assistName: assistName,
                    teamName: teamName,
                    monthlyReceipt: 0,
                    monthlyCount: 0,
                    dailyReceipt: 0,
                    dailyCount: 0
                  };
                }
                
                assistMonthlyData[key].monthlyReceipt += adjustedAmount;
                assistMonthlyData[key].monthlyCount += count;
                
                // 당일 데이터인 경우 별도 집계
                if (docDate === currentDateFormatted) {
                  assistMonthlyData[key].dailyReceipt += adjustedAmount;
                  assistMonthlyData[key].dailyCount += count;
                }
              });
            }
          }
        });

        // 배열로 변환하고 정렬
        const sortedData = Object.values(assistMonthlyData)
          .filter(item => item.monthlyReceipt > 0)
          .sort((a, b) => b.monthlyReceipt - a.monthlyReceipt);
        
        setMonthlyData(sortedData);
        
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `✅ [어시스트별 월 수납] ${currentYearMonth} 로드 완료:`, {
          processedDocs,
          assistCount: sortedData.length,
          totalReceipt: sortedData.reduce((sum, item) => sum + item.monthlyReceipt, 0)
        });
        
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '어시스트별 월 수납 현황 로드 실패:', error);
        setError('데이터 로드 중 오류가 발생했습니다.');
      } finally {
        DailyReportDebugLogger.loadingState('END', 'saveError catch 블록');
        setIsLoading(false);
      }
    };

    loadMonthlyAssistData();
  }, [currentYearMonth, currentDateFormatted, forceRefreshKey, employees, budgetData]);


  if (isLoading) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
            👥 {currentYearMonth}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">어시스트별 월별 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
            👥 {currentYearMonth}
          </div>
        </div>
        <Alert type="error" title="오류">
          {error}
        </Alert>
      </div>
    );
  }

  const totalMonthlyReceipt = monthlyData.reduce((sum, item) => sum + item.monthlyReceipt, 0);
  const totalMonthlyCount = monthlyData.reduce((sum, item) => sum + item.monthlyCount, 0);
  const totalDailyReceipt = monthlyData.reduce((sum, item) => sum + item.dailyReceipt, 0);
  const totalDailyCount = monthlyData.reduce((sum, item) => sum + item.dailyCount, 0);

  const tableData = monthlyData.map((item, index) => ({
    '어시스트': item.assistName,
    '팀명': item.teamName,
    '월별 건수': item.monthlyCount,
    '월별 수납액': item.monthlyReceipt,
    '당일 건수': item.dailyCount,
    '당일 수납액': item.dailyReceipt,
    uniqueKey: `assist-monthly-${index}`
  }));

  const footerData = {
    '어시스트': '합계',
    '팀명': '',
    '월별 건수': totalMonthlyCount.toLocaleString(),
    '월별 수납액': totalMonthlyReceipt.toLocaleString(),
    '당일 건수': totalDailyCount.toLocaleString(),
    '당일 수납액': totalDailyReceipt.toLocaleString()
  };

  return (
    <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 월 수납 현황</h2>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200">
            👥 {currentYearMonth}
          </div>
          <button
            onClick={forceClearCacheAndRefresh}
            className="inline-flex items-center px-3 py-1 bg-red-50 text-red-700 text-sm font-medium rounded-full border border-red-200 hover:bg-red-100 transition-colors"
            title="캐시 클리어 및 강제 새로고침"
          >
            🔄 새로고침
          </button>
        </div>
      </div>
      
      {monthlyData.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">어시스트별 월별 데이터가 없습니다</h3>
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
            <span className="font-medium text-gray-700">{currentYearMonth}</span>월에 집계된 어시스트별 수납 데이터가 없습니다.
            <br className="hidden sm:block" />
            데일리 리포트 데이터를 먼저 입력해주세요.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                <DataTable 
                  data={tableData}
                  columns={[
                    { key: '어시스트', header: '어시스트', sortable: true, width: 'w-28 sm:w-32' },
                    { key: '팀명', header: '팀명', sortable: true, width: 'w-20 sm:w-24', render: (value) => (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        value === '미지정' 
                          ? 'bg-gray-100 text-gray-600' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {value}
                      </span>
                    ) },
                    { key: '월별 건수', header: '월별 건수', align: 'right', sortable: true, width: 'w-20 sm:w-24', render: (value) => <span className="font-mono text-sm">{typeof value === 'number' ? value.toLocaleString() : value}</span> },
                    { key: '월별 수납액', header: '월별 수납액', align: 'right', sortable: true, width: 'w-28 sm:w-32', render: (value) => <span className="font-mono text-sm font-medium">{typeof value === 'number' ? value.toLocaleString() + '원' : value}</span> },
                    { key: '당일 건수', header: '당일 건수', align: 'right', sortable: true, width: 'w-20 sm:w-24', render: (value) => <span className="font-mono text-sm">{typeof value === 'number' ? value.toLocaleString() : value}</span> },
                    { key: '당일 수납액', header: '당일 수납액', align: 'right', sortable: true, width: 'w-28 sm:w-32', render: (value) => <span className="font-mono text-sm">{typeof value === 'number' ? value.toLocaleString() + '원' : value}</span> }
                  ]}
                  keyField="uniqueKey"
                  showFooter={true}
                  footerData={footerData}
                  sortable={true}
                />
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm sm:text-base text-blue-700 font-medium">
                총 {monthlyData.length}명의 어시스트 월별 집계가 완료되었습니다.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm sm:text-base">
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">월별 총건수</div>
                <div className="font-mono font-bold text-blue-700">{totalMonthlyCount.toLocaleString()}건</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">월별 총수납액</div>
                <div className="font-mono font-bold text-blue-700">{totalMonthlyReceipt.toLocaleString()}원</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">당일 총건수</div>
                <div className="font-mono font-bold text-green-600">{totalDailyCount.toLocaleString()}건</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">당일 총수납액</div>
                <div className="font-mono font-bold text-green-600">{totalDailyReceipt.toLocaleString()}원</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// 🆕 월별 거래관리 합계 컴포넌트 (v2: AppContext 사용)
const MonthlyTransactionSummary = ({
  currentDate
}: {
  currentDate: string;
}) => {
  // v2: AppContext에서 budgetData, employees 가져오기 (기존 패턴과 동일)
  const { budgetData, employees } = useAppContext();

  const [summaryData, setSummaryData] = React.useState<MonthlyTransactionSummaryType | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(true);

  const yearMonth = currentDate.substring(0, 7); // "YYYY-MM"

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // v2: employees, budgetData 전달
        const data = await getMonthlyFinancialTransactionData(yearMonth, employees, budgetData);
        setSummaryData(data);
      } catch (error) {
        console.error('월별 거래 데이터 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // v2: employees가 로드된 후에만 실행
    if (employees && employees.length > 0) {
      loadData();
    }
  }, [yearMonth, employees, budgetData]); // v2: 의존성 배열에 employees, budgetData 추가

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString() + '원';
  };

  if (isLoading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <div className="animate-pulse text-gray-500">월별 거래 데이터 로딩 중...</div>
      </div>
    );
  }

  if (!summaryData || summaryData.itemCount === 0) {
    return (
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          📊 월별 거래관리 합계 ({yearMonth})
        </h3>
        <p className="text-gray-500">해당 월의 거래 데이터가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-4">
      {/* 헤더 */}
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="text-lg font-semibold text-gray-800">
          📊 월별 거래관리 합계 ({yearMonth})
        </h3>
        <span className="text-gray-500">{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {/* 요약 카드 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <div className="text-sm text-red-600">총 지출</div>
              <div className="text-lg font-bold text-red-700">
                💸 {formatCurrency(summaryData.totalExpense)}
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="text-sm text-green-600">총 수입</div>
              <div className="text-lg font-bold text-green-700">
                💰 {formatCurrency(summaryData.totalIncome)}
              </div>
            </div>
            <div className={`p-3 rounded-lg border ${
              summaryData.netAmount >= 0
                ? 'bg-blue-50 border-blue-200'
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="text-sm text-gray-600">순 금액</div>
              <div className={`text-lg font-bold ${
                summaryData.netAmount >= 0 ? 'text-blue-700' : 'text-orange-700'
              }`}>
                ✨ {summaryData.netAmount >= 0 ? '+' : ''}{formatCurrency(summaryData.netAmount)}
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">거래 건수</div>
              <div className="text-lg font-bold text-gray-700">
                📋 {summaryData.itemCount}건
              </div>
            </div>
          </div>

          {/* 카테고리별 분류 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 지출 카테고리 */}
            <div className="bg-red-50 p-3 rounded-lg">
              <h4 className="font-medium text-red-700 mb-2">💸 지출 카테고리</h4>
              {Object.keys(summaryData.expenseByCategory).length > 0 ? (
                <ul className="space-y-1">
                  {Object.entries(summaryData.expenseByCategory)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, amount]) => (
                      <li key={category} className="flex justify-between text-sm">
                        <span className="text-red-600">{category}</span>
                        <span className="font-medium text-red-700">{formatCurrency(amount)}</span>
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-sm text-red-400">지출 내역 없음</p>
              )}
            </div>

            {/* 수입 카테고리 */}
            <div className="bg-green-50 p-3 rounded-lg">
              <h4 className="font-medium text-green-700 mb-2">💰 수입 카테고리</h4>
              {Object.keys(summaryData.incomeByCategory).length > 0 ? (
                <ul className="space-y-1">
                  {Object.entries(summaryData.incomeByCategory)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, amount]) => (
                      <li key={category} className="flex justify-between text-sm">
                        <span className="text-green-600">{category}</span>
                        <span className="font-medium text-green-700">{formatCurrency(amount)}</span>
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-sm text-green-400">수입 내역 없음</p>
              )}
            </div>
          </div>

          {/* 팀별 거래 현황 */}
          {Object.keys(summaryData.transactionsByTeam).length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2">👥 팀별 거래 현황</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">팀</th>
                      <th className="px-3 py-2 text-right text-red-600">지출</th>
                      <th className="px-3 py-2 text-right text-green-600">수입</th>
                      <th className="px-3 py-2 text-right">순액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(summaryData.transactionsByTeam)
                      .sort((a, b) => (b[1].income - b[1].expense) - (a[1].income - a[1].expense))
                      .map(([team, data]) => {
                        const netAmount = data.income - data.expense;
                        return (
                          <tr key={team} className="border-b">
                            <td className="px-3 py-2 font-medium">{team}</td>
                            <td className="px-3 py-2 text-right text-red-600">
                              {formatCurrency(data.expense)}
                            </td>
                            <td className="px-3 py-2 text-right text-green-600">
                              {formatCurrency(data.income)}
                            </td>
                            <td className={`px-3 py-2 text-right font-medium ${
                              netAmount >= 0 ? 'text-blue-600' : 'text-orange-600'
                            }`}>
                              {netAmount >= 0 ? '+' : ''}{formatCurrency(netAmount)}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 🆕 어시스트별 내원경로 월 수납 현황 컴포넌트 (referralReceipts 데이터 사용)
const MonthlyReferralPathReport = ({
  currentDate
}: {
  currentDate: string;
}) => {
  const [monthlyData, setMonthlyData] = React.useState<Array<{
    assistName: string;
    referralPath: string;
    monthlyReceipt: number;
    monthlyCount: number;
    dailyReceipt: number;
    dailyCount: number;
  }>>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  
  const currentYearMonth = currentDate.substring(0, 7); // YYYY-MM 형식
  const currentDateFormatted = formatDate(new Date(currentDate));

  React.useEffect(() => {
    const loadMonthlyReferralData = async () => {
      try {
        DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
        setError(null);
        
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `🔍 [어시스트별 내원경로 월 수납] ${currentYearMonth} 데이터 로드 시작`);
        
        // Firebase에서 해당 월의 모든 referralReceipts 데이터 가져오기
        // Firebase imports moved to top
        
        const reportsCol = collection(db, 'dailyReports');
        const snapshot = await getDocs(reportsCol);
        
        const referralMonthlyData: Record<string, {
          assistName: string;
          referralPath: string;
          monthlyReceipt: number;
          monthlyCount: number;
          dailyReceipt: number;
          dailyCount: number;
        }> = {};

        let processedDocs = 0;

        // 디버깅: 원본 데이터 수집
        const DEBUG_MODE = localStorage.getItem('DEBUG_DAILY_REPORT') === 'true';
        const allReferralReceipts: any[] = [];

        snapshot.docs.forEach(doc => {
          const docDate = doc.id; // YYYY-MM-DD

          if (docDate.startsWith(currentYearMonth)) {
            processedDocs++;
            const data = doc.data();

            // 디버깅: 원본 referralReceipts 수집
            if (DEBUG_MODE && data && data.referralReceipts) {
              allReferralReceipts.push(...data.referralReceipts);
            }

            // referralReceipts에서 어시스트별 내원경로 데이터 집계
            if (data && data.referralReceipts && Array.isArray(data.referralReceipts)) {
              data.referralReceipts.forEach((receipt: ReferralReceiptItem) => {
                const assistName = receipt.assistName || '미지정';
                const referralPath = receipt.referralPath || '미지정';
                const amount = receipt.amount || 0;
                const count = receipt.count || 1;

                const key = `${assistName}-${referralPath}`;

                if (!referralMonthlyData[key]) {
                  referralMonthlyData[key] = {
                    assistName: assistName,
                    referralPath: referralPath,
                    monthlyReceipt: 0,
                    monthlyCount: 0,
                    dailyReceipt: 0,
                    dailyCount: 0
                  };
                }

                referralMonthlyData[key].monthlyReceipt += amount;
                referralMonthlyData[key].monthlyCount += count;
                
                // 당일 데이터인 경우 별도 집계
                if (docDate === currentDateFormatted) {
                  referralMonthlyData[key].dailyReceipt += amount;
                  referralMonthlyData[key].dailyCount += count;
                }
              });
            }
            
            // financialTransactions 데이터도 집계에 포함 (카테고리를 내원경로로 매핑)
            if (data && data.financialTransactions && data.financialTransactions.items) {
              data.financialTransactions.items.forEach((transaction: FinancialTransactionItem) => {
                const assistName = transaction.assistName || '미지정';
                const referralPath = `거래-${transaction.category || '기타'}`;
                const amount = transaction.amount || 0;
                const count = 1;
                
                // 거래 유형에 따라 금액 조정 (지출은 마이너스)
                const adjustedAmount = transaction.transactionType === 'expense' ? -amount : amount;
                
                const key = `${assistName}-${referralPath}`;
                
                if (!referralMonthlyData[key]) {
                  referralMonthlyData[key] = {
                    assistName: assistName,
                    referralPath: referralPath,
                    monthlyReceipt: 0,
                    monthlyCount: 0,
                    dailyReceipt: 0,
                    dailyCount: 0
                  };
                }
                
                referralMonthlyData[key].monthlyReceipt += adjustedAmount;
                referralMonthlyData[key].monthlyCount += count;
                
                // 당일 데이터인 경우 별도 집계
                if (docDate === currentDateFormatted) {
                  referralMonthlyData[key].dailyReceipt += adjustedAmount;
                  referralMonthlyData[key].dailyCount += count;
                }
              });
            }
          }
        });

        // 배열로 변환하고 정렬 (어시스트명 → 내원경로 순)
        const sortedData = Object.values(referralMonthlyData)
          .filter(item => item.monthlyReceipt > 0)
          .sort((a, b) => {
            if (a.assistName !== b.assistName) {
              return a.assistName.localeCompare(b.assistName);
            }
            return b.monthlyReceipt - a.monthlyReceipt;
          });
        
        setMonthlyData(sortedData);
        
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `✅ [어시스트별 내원경로 월 수납] ${currentYearMonth} 로드 완료:`, {
          processedDocs,
          itemCount: sortedData.length,
          totalReceipt: sortedData.reduce((sum, item) => sum + item.monthlyReceipt, 0)
        });
        
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '어시스트별 내원경로 월 수납 현황 로드 실패:', error);
        setError('데이터 로드 중 오류가 발생했습니다.');
      } finally {
        DailyReportDebugLogger.loadingState('END', 'firebaseError catch 블록');
        setIsLoading(false);
      }
    };

    loadMonthlyReferralData();
  }, [currentYearMonth, currentDateFormatted]);

  const headers = ['어시스트', '내원경로', '월별 건수', '월별 수납액', '당일 건수', '당일 수납액'];

  if (isLoading) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 내원경로 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full border border-green-200">
            🔗 {currentYearMonth}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">내원경로별 월별 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 내원경로 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full border border-green-200">
            🔗 {currentYearMonth}
          </div>
        </div>
        <Alert type="error" title="오류">
          {error}
        </Alert>
      </div>
    );
  }

  // 어시스트별 그룹화 (소계 표시용)
  const assistGroups = monthlyData.reduce((groups, item) => {
    if (!groups[item.assistName]) {
      groups[item.assistName] = {
        monthlyReceipt: 0,
        monthlyCount: 0,
        dailyReceipt: 0,
        dailyCount: 0,
        items: []
      };
    }
    groups[item.assistName].monthlyReceipt += item.monthlyReceipt;
    groups[item.assistName].monthlyCount += item.monthlyCount;
    groups[item.assistName].dailyReceipt += item.dailyReceipt;
    groups[item.assistName].dailyCount += item.dailyCount;
    groups[item.assistName].items.push(item);
    return groups;
  }, {} as Record<string, { monthlyReceipt: number; monthlyExpense: number; compositeKey: string; teamName: string }>);

  const totalMonthlyReceipt = monthlyData.reduce((sum, item) => sum + item.monthlyReceipt, 0);
  const totalMonthlyCount = monthlyData.reduce((sum, item) => sum + item.monthlyCount, 0);
  const totalDailyReceipt = monthlyData.reduce((sum, item) => sum + item.dailyReceipt, 0);
  const totalDailyCount = monthlyData.reduce((sum, item) => sum + item.dailyCount, 0);

  return (
    <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 내원경로 월 수납 현황</h2>
        <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full border border-green-200">
          🔗 {currentYearMonth}
        </div>
      </div>
      
      {monthlyData.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">내원경로별 월별 데이터가 없습니다</h3>
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
            <span className="font-medium text-gray-700">{currentYearMonth}</span>월에 집계된 내원경로별 수납 데이터가 없습니다.
            <br className="hidden sm:block" />
            데일리 리포트 데이터를 먼저 입력해주세요.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {headers.map(header => (
                        <th key={header} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.keys(assistGroups).sort().map(assistName => {
                      const group = assistGroups[assistName];
                      return (
                        <React.Fragment key={assistName}>
                          {group.items.map((item: any, index: number) => (
                            <tr key={`${item.assistName}-${item.referralPath}`} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-200">
                                {index === 0 ? (
                                  <span className="font-medium text-gray-900">{assistName}</span>
                                ) : (
                                  <span className="text-gray-400">⌊</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">{item.referralPath}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono border-r border-gray-200">{item.monthlyCount.toLocaleString()}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-medium border-r border-gray-200">
                                {item.monthlyReceipt.toLocaleString()}원
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono border-r border-gray-200">{item.dailyCount.toLocaleString()}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono">
                                {item.dailyReceipt.toLocaleString()}원
                              </td>
                            </tr>
                          ))}
                          {/* 어시스트별 소계 */}
                          <tr className="bg-blue-50">
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-blue-900 border-r border-gray-200">{assistName} 소계</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-blue-700 border-r border-gray-200">-</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-medium text-blue-900 border-r border-gray-200">{group.monthlyCount.toLocaleString()}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-blue-900 border-r border-gray-200">{group.monthlyReceipt.toLocaleString()}원</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-medium text-blue-900 border-r border-gray-200">{group.dailyCount.toLocaleString()}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-blue-900">{group.dailyReceipt.toLocaleString()}원</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                    {/* 전체 합계 */}
                    <tr className="bg-green-50 font-bold">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-green-900 border-r border-gray-200">전체 합계</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-green-700 border-r border-gray-200">-</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-green-900 border-r border-gray-200">
                        {totalMonthlyCount.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-green-900 border-r border-gray-200">
                        {totalMonthlyReceipt.toLocaleString()}원
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-green-900 border-r border-gray-200">
                        {totalDailyCount.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-green-900">
                        {totalDailyReceipt.toLocaleString()}원
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 sm:p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm sm:text-base text-green-700 font-medium">
                총 {Object.keys(assistGroups).length}명의 어시스트, {monthlyData.length}개의 내원경로 조합이 집계되었습니다.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm sm:text-base">
              <div className="bg-white p-3 rounded-lg border border-green-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">월별 총건수</div>
                <div className="font-mono font-bold text-green-700">{totalMonthlyCount.toLocaleString()}건</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-green-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">월별 총수납액</div>
                <div className="font-mono font-bold text-green-700">{totalMonthlyReceipt.toLocaleString()}원</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-green-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">당일 총건수</div>
                <div className="font-mono font-bold text-emerald-600">{totalDailyCount.toLocaleString()}건</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-green-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">당일 총수납액</div>
                <div className="font-mono font-bold text-emerald-600">{totalDailyReceipt.toLocaleString()}원</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// 🆕 어시스트별 진료구분 월 수납 현황 컴포넌트 (assistReceipts 데이터 사용)
const MonthlyTreatmentTypeReport = ({ 
  currentDate
}: {
  currentDate: string;
}) => {
  const [monthlyData, setMonthlyData] = React.useState<Array<{
    assistName: string;
    treatmentType: string;
    monthlyReceipt: number;
    monthlyCount: number;
    dailyReceipt: number;
    dailyCount: number;
  }>>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  
  const currentYearMonth = currentDate.substring(0, 7); // YYYY-MM 형식
  const currentDateFormatted = formatDate(new Date(currentDate));

  React.useEffect(() => {
    const loadMonthlyTreatmentData = async () => {
      try {
        DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
        setError(null);
        
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `🔍 [어시스트별 진료구분 월 수납] ${currentYearMonth} 데이터 로드 시작`);
        
        // Firebase에서 해당 월의 모든 assistReceipts 데이터 가져오기
        // Firebase imports moved to top
        
        const reportsCol = collection(db, 'dailyReports');
        const snapshot = await getDocs(reportsCol);
        
        const treatmentMonthlyData: Record<string, {
          assistName: string;
          treatmentType: string;
          monthlyReceipt: number;
          monthlyCount: number;
          dailyReceipt: number;
          dailyCount: number;
        }> = {};
        
        let processedDocs = 0;
        
        snapshot.docs.forEach(doc => {
          const docDate = doc.id; // YYYY-MM-DD
          
          if (docDate.startsWith(currentYearMonth)) {
            processedDocs++;
            const data = doc.data();
            
            // assistReceipts에서 어시스트별 진료구분 데이터 집계
            if (data && data.assistReceipts && Array.isArray(data.assistReceipts)) {
              data.assistReceipts.forEach((receipt: AssistReceiptItem) => {
                const assistName = receipt.assistName || '미지정';
                const treatmentType = receipt.treatmentType || '미지정';
                const amount = receipt.amount || 0;
                const count = receipt.count || 1;
                
                // 복합키 처리 (compositeKey가 있는 경우)
                let finalAssistName = assistName;
                
                if (receipt.compositeKey) {
                  const [originalAssistName] = receipt.compositeKey.split('-');
                  finalAssistName = originalAssistName || assistName;
                }
                
                const key = `${finalAssistName}-${treatmentType}`;
                
                if (!treatmentMonthlyData[key]) {
                  treatmentMonthlyData[key] = {
                    assistName: finalAssistName,
                    treatmentType: treatmentType,
                    monthlyReceipt: 0,
                    monthlyCount: 0,
                    dailyReceipt: 0,
                    dailyCount: 0
                  };
                }
                
                treatmentMonthlyData[key].monthlyReceipt += amount;
                treatmentMonthlyData[key].monthlyCount += count;
                
                // 당일 데이터인 경우 별도 집계
                if (docDate === currentDateFormatted) {
                  treatmentMonthlyData[key].dailyReceipt += amount;
                  treatmentMonthlyData[key].dailyCount += count;
                }
              });
            }
            
            // financialTransactions 데이터도 집계에 포함 (거래 유형과 카테고리를 진료구분으로 매핑)
            if (data && data.financialTransactions && data.financialTransactions.items) {
              data.financialTransactions.items.forEach((transaction: FinancialTransactionItem) => {
                const assistName = transaction.assistName || '미지정';
                const treatmentType = `거래-${transaction.transactionType}-${transaction.category || '기타'}`;
                const amount = transaction.amount || 0;
                const count = 1;
                
                // 거래 유형에 따라 금액 조정 (지출은 마이너스)
                const adjustedAmount = transaction.transactionType === 'expense' ? -amount : amount;
                
                const key = `${assistName}-${treatmentType}`;
                
                if (!treatmentMonthlyData[key]) {
                  treatmentMonthlyData[key] = {
                    assistName: assistName,
                    treatmentType: treatmentType,
                    monthlyReceipt: 0,
                    monthlyCount: 0,
                    dailyReceipt: 0,
                    dailyCount: 0
                  };
                }
                
                treatmentMonthlyData[key].monthlyReceipt += adjustedAmount;
                treatmentMonthlyData[key].monthlyCount += count;
                
                // 당일 데이터인 경우 별도 집계
                if (docDate === currentDateFormatted) {
                  treatmentMonthlyData[key].dailyReceipt += adjustedAmount;
                  treatmentMonthlyData[key].dailyCount += count;
                }
              });
            }
          }
        });
        
        // 배열로 변환하고 정렬 (어시스트명 → 진료구분 순)
        const sortedData = Object.values(treatmentMonthlyData)
          .filter(item => item.monthlyReceipt > 0)
          .sort((a, b) => {
            if (a.assistName !== b.assistName) {
              return a.assistName.localeCompare(b.assistName);
            }
            return b.monthlyReceipt - a.monthlyReceipt;
          });
        
        setMonthlyData(sortedData);
        
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', `✅ [어시스트별 진료구분 월 수납] ${currentYearMonth} 로드 완료:`, {
          processedDocs,
          itemCount: sortedData.length,
          totalReceipt: sortedData.reduce((sum, item) => sum + item.monthlyReceipt, 0)
        });
        
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '어시스스별 진료구분 월 수납 현황 로드 실패:', error);
        setError('데이터 로드 중 오류가 발생했습니다.');
      } finally {
        DailyReportDebugLogger.loadingState('END', 'finally 블록');
        setIsLoading(false);
      }
    };

    loadMonthlyTreatmentData();
  }, [currentYearMonth, currentDateFormatted]);

  const headers = ['어시스트', '진료구분', '월별 건수', '월별 수납액', '당일 건수', '당일 수납액'];

  if (isLoading) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 진료구분 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-700 text-sm font-medium rounded-full border border-orange-200">
            🏥 {currentYearMonth}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-600 mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">진료구분별 월별 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 진료구분 월 수납 현황</h2>
          <div className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-700 text-sm font-medium rounded-full border border-orange-200">
            🏥 {currentYearMonth}
          </div>
        </div>
        <Alert type="error" title="오류">
          {error}
        </Alert>
      </div>
    );
  }

  // 어시스트별 그룹화 (소계 표시용)
  const assistGroups = monthlyData.reduce((groups, item) => {
    if (!groups[item.assistName]) {
      groups[item.assistName] = {
        monthlyReceipt: 0,
        monthlyCount: 0,
        dailyReceipt: 0,
        dailyCount: 0,
        items: []
      };
    }
    groups[item.assistName].monthlyReceipt += item.monthlyReceipt;
    groups[item.assistName].monthlyCount += item.monthlyCount;
    groups[item.assistName].dailyReceipt += item.dailyReceipt;
    groups[item.assistName].dailyCount += item.dailyCount;
    groups[item.assistName].items.push(item);
    return groups;
  }, {} as Record<string, { monthlyReceipt: number; monthlyExpense: number; compositeKey: string; teamName: string }>);

  const totalMonthlyReceipt = monthlyData.reduce((sum, item) => sum + item.monthlyReceipt, 0);
  const totalMonthlyCount = monthlyData.reduce((sum, item) => sum + item.monthlyCount, 0);
  const totalDailyReceipt = monthlyData.reduce((sum, item) => sum + item.dailyReceipt, 0);
  const totalDailyCount = monthlyData.reduce((sum, item) => sum + item.dailyCount, 0);

  return (
    <div className="mb-6 sm:mb-8 bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">어시스트별 진료구분 월 수납 현황</h2>
        <div className="inline-flex items-center px-3 py-1 bg-orange-50 text-orange-700 text-sm font-medium rounded-full border border-orange-200">
          🏥 {currentYearMonth}
        </div>
      </div>
      
      {monthlyData.length === 0 ? (
        <div className="text-center py-12 sm:py-16">
          <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">진료구분별 월별 데이터가 없습니다</h3>
          <p className="text-gray-500 text-sm sm:text-base max-w-md mx-auto">
            <span className="font-medium text-gray-700">{currentYearMonth}</span>월에 집계된 진료구분별 수납 데이터가 없습니다.
            <br className="hidden sm:block" />
            데일리 리포트 데이터를 먼저 입력해주세요.
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden border border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {headers.map(header => (
                        <th key={header} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 last:border-r-0">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Object.keys(assistGroups).sort().map(assistName => {
                      const group = assistGroups[assistName];
                      return (
                        <React.Fragment key={assistName}>
                          {group.items.map((item: any, index: number) => (
                            <tr key={`${item.assistName}-${item.treatmentType}`} className="hover:bg-gray-50">
                              <td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-200">
                                {index === 0 ? (
                                  <span className="font-medium text-gray-900">{assistName}</span>
                                ) : (
                                  <span className="text-gray-400">⌊</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-200">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  item.treatmentType === '미지정' 
                                    ? 'bg-gray-100 text-gray-600' 
                                    : 'bg-orange-100 text-orange-800'
                                }`}>
                                  {item.treatmentType}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono border-r border-gray-200">{item.monthlyCount.toLocaleString()}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-medium border-r border-gray-200">
                                {item.monthlyReceipt.toLocaleString()}원
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono border-r border-gray-200">{item.dailyCount.toLocaleString()}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono">
                                {item.dailyReceipt.toLocaleString()}원
                              </td>
                            </tr>
                          ))}
                          {/* 어시스트별 소계 */}
                          <tr className="bg-blue-50">
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-blue-900 border-r border-gray-200">{assistName} 소계</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-blue-700 border-r border-gray-200">-</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-medium text-blue-900 border-r border-gray-200">{group.monthlyCount.toLocaleString()}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-blue-900 border-r border-gray-200">{group.monthlyReceipt.toLocaleString()}원</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-medium text-blue-900 border-r border-gray-200">{group.dailyCount.toLocaleString()}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-blue-900">{group.dailyReceipt.toLocaleString()}원</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                    {/* 전체 합계 */}
                    <tr className="bg-orange-50 font-bold">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-orange-900 border-r border-gray-200">전체 합계</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-center text-orange-700 border-r border-gray-200">-</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-orange-900 border-r border-gray-200">
                        {totalMonthlyCount.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-orange-900 border-r border-gray-200">
                        {totalMonthlyReceipt.toLocaleString()}원
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-orange-900 border-r border-gray-200">
                        {totalDailyCount.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-mono font-bold text-orange-900">
                        {totalDailyReceipt.toLocaleString()}원
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-4 sm:p-6 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm sm:text-base text-orange-700 font-medium">
                총 {Object.keys(assistGroups).length}명의 어시스트, {monthlyData.length}개의 진료구분 조합이 집계되었습니다.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-sm sm:text-base">
              <div className="bg-white p-3 rounded-lg border border-orange-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">월별 총건수</div>
                <div className="font-mono font-bold text-orange-700">{totalMonthlyCount.toLocaleString()}건</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-orange-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">월별 총수납액</div>
                <div className="font-mono font-bold text-orange-700">{totalMonthlyReceipt.toLocaleString()}원</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-orange-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">당일 총건수</div>
                <div className="font-mono font-bold text-amber-600">{totalDailyCount.toLocaleString()}건</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-orange-100">
                <div className="text-gray-600 text-xs sm:text-sm mb-1">당일 총수납액</div>
                <div className="font-mono font-bold text-amber-600">{totalDailyReceipt.toLocaleString()}원</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// 어시스트별 수납 현황 컴포넌트
const DailyReportPage: React.FC = () => {
  // Context에서 직원 데이터 및 예산 데이터 가져오기
  const { budgetData, setBudgetData, triggerActualSalesRefreshAfterExcelUpload, teams, employees } = useAppContext();
  const { user } = useAuth();
  
  // 상태 관리
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [file, setFile] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // 🚀 성능 최적화 훅 적용
  const formattedCurrentDate = formatDate(currentDate);
  const currentYearMonthForBanner = formattedCurrentDate.substring(0, 7);

  // 🛡️ 이 달에 명세가 무너진 날이 있는지 — 상단 안내용. 저장 차단과 같은 판정(findCollapsedDays)을 쓴다.
  //    ⚠️ 반드시 이 컴포넌트(DailyReportPage) 안에 있어야 한다. 처음에 MonthlyTransactionSummary 에
  //    넣고 여기 렌더에서 썼더니, tsconfig 이 없어 타입 검사 없이 컴파일된 채 배포돼 ReferenceError 로
  //    화면이 통째로 비었다(2026-09-07). verify-hook-scope.mjs 가 이 실수를 잡는다.
  const [collapsedDaysOfMonth, setCollapsedDaysOfMonth] = useState<CollapsedDay[]>([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { getMonthlyCollapsedDays } = await import('../services/firebase/monthlyDataFirebaseService.ts');
        const days = await getMonthlyCollapsedDays(currentYearMonthForBanner);
        if (alive) setCollapsedDaysOfMonth(days);
      } catch (e) {
        if (alive) setCollapsedDaysOfMonth([]);
      }
    })();
    return () => { alive = false; };
  }, [currentYearMonthForBanner]);
  const {
    isOptimizing
  } = useDailyReportOptimization(formattedCurrentDate);
  
  const [receiptData, setReceiptData] = useState<ReceiptData>({
    total: 0,
    items: []
  });
  
  const [expenseData, setExpenseData] = useState<ExpenseData>({
    total: 0,
    items: []
  });
  
  // 새로운 통합 거래 관리 상태
  const [financialTransactionData, setFinancialTransactionData] = useState<FinancialTransactionData>({
    totalExpense: 0,
    totalIncome: 0,
    netAmount: 0,
    items: []
  });

  // 🔧 엑셀 업로드 중 거래 데이터 초기화 방지용 플래그
  const [isExcelUploading, setIsExcelUploading] = useState(false);

  // 통합모드 기본 사용 (상태 제거됨)
  
  // const [reportStatusData, setReportStatusData] = useState<ReportStatusItem[]>([]); // 현재 사용하지 않음

  // 필수 상태 변수들 - 월별 집계 및 엑셀 업로드에서 사용됨
  const [assistReceiptData, setAssistReceiptData] = useState<AssistReceiptItem[]>([]);
  const [referralReceiptData, setReferralReceiptData] = useState<ReferralReceiptItem[]>([]);
  
  // 진료의사 매핑 관련 상태 - 미사용
  // const [showDoctorMappingModal, setShowDoctorMappingModal] = useState(false);
  // const [unmatchedDoctors, setUnmatchedDoctors] = useState<string[]>([]);
  
  // 팀별 수납 현황 데이터 생성 함수를 저장하는 상태 - 제거됨
  // const [getTeamCollectionData, setGetTeamCollectionData] = useState<(() => Promise<any[]>) | null>(null);

  // 이전 날짜로 이동
  const handlePrevDay = () => {
    setCurrentDate(getPreviousDay(currentDate));
  };
  
  // 다음 날짜로 이동
  const handleNextDay = () => {
    setCurrentDate(getNextDay(currentDate));
  };

  // 날짜 변경 핸들러 추가
  const handleDateChange = (newDate: string) => {
    const selectedDate = new Date(newDate);
    setCurrentDate(selectedDate);
  };
  
  const { processFile } = useExcelProcessor();
  const { exportToExcel, isExporting } = useExcelExporter();

  // 엑셀 파일 다운로드 처리
  const handleFileDownload = async () => {
    try {
      // 현재 데이터 수집
      const exportData = {
        receiptData,
        assistReceiptData,
        referralReceiptData,
        currentDate: formattedCurrentDate
      };

      // 데이터 유효성 검사
      if (!receiptData.items || receiptData.items.length === 0) {
        alert('다운로드할 데이터가 없습니다. 먼저 엑셀 파일을 업로드해주세요.');
        return;
      }

      // 엑셀 파일 생성 및 다운로드
      await exportToExcel(exportData);

    } catch (error) {
      console.error('❌ [Excel Download] 다운로드 실패:', error);
      alert(`엑셀 다운로드 중 오류가 발생했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  };

  // 엑셀 파일 업로드 처리
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    debugLog('EXCEL_UPLOAD', '🚀 [엑셀 업로드 시작] 파일 선택됨');

    // 🔧 엑셀 업로드 시작 시 플래그 설정 (거래 데이터 초기화 방지)
    setIsExcelUploading(true);

    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) {
      debugLog('EXCEL_UPLOAD', '❌ [엑셀 업로드] 파일이 선택되지 않음');
      setIsExcelUploading(false); // 실패 시 플래그 해제
      return;
    }
    
    debugLog('EXCEL_UPLOAD', '📁 [엑셀 업로드] 파일 정보:', {
      name: uploadedFile.name,
      size: uploadedFile.size,
      type: uploadedFile.type,
      lastModified: new Date(uploadedFile.lastModified).toLocaleString()
    });
    
    // 파일 확장자 검증
    const fileExtension = uploadedFile.name.split('.').pop()?.toLowerCase();
    debugLog('EXCEL_UPLOAD', '🔍 [엑셀 업로드] 파일 확장자 확인:', fileExtension);
    
    if (fileExtension !== 'xlsx' && fileExtension !== 'xls') {
      debugLog('EXCEL_UPLOAD', '❌ [엑셀 업로드] 지원하지 않는 파일 형식:', fileExtension);
      alert('지원하지 않는 파일 형식입니다. Excel 파일(.xlsx, .xls)만 업로드 가능합니다.');
      e.target.value = ''; // 파일 선택 초기화
      return;
    }
    
    setFile(uploadedFile);
    
    // 🆕 Excel 파일 업로드 시 캐시 초기화
    debugLog('EXCEL_UPLOAD', '[Excel 파일 업로드] 캐시 초기화 시작');
    
    // TeamCollectionService 캐시 초기화
    const teamCollectionServiceInstance = new TeamCollectionService();
    teamCollectionServiceInstance.clearCache();
    
    // 현재 날짜의 월별 데이터 캐시 무효화
    const currentMonth = formatDate(currentDate).substring(0, 7);
    teamCollectionServiceInstance.invalidateCache({ 
      yearMonth: currentMonth, 
      monthlyData: true 
    });
    
    debugLog('EXCEL_UPLOAD', '[Excel 파일 업로드] 캐시 초기화 완료');
    
    // 현재 시간 설정
    const now = new Date();
    const timeString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    
    // 파일 읽기
    debugLog('EXCEL_UPLOAD', '📖 [엑셀 업로드] 파일 읽기 시작, 로딩 상태 설정');
    DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
    
    try {
      debugLog('EXCEL_UPLOAD', '⚙️ [엑셀 업로드] useExcelProcessor 훅으로 파일 처리 시작');
      // useExcelProcessor 훅 사용
      const processedData = await processFile(uploadedFile);
      debugLog('EXCEL_UPLOAD', '✅ [엑셀 업로드] 파일 처리 완료:', {
        receiptItemsCount: processedData.receiptData?.items?.length || 0,
        assistReceiptDataCount: processedData.assistReceiptData?.length || 0,
        assistCashierDoctorDataCount: processedData.assistCashierDoctorData?.length || 0,
        referralReceiptDataCount: processedData.referralReceiptData?.length || 0,
        validationErrorsCount: processedData.validationErrors?.length || 0
      });
      
      // 현재 월의 직원 데이터 가져오기
      const currentMonth = formatDate(currentDate).substring(0, 7);
      let employees: any[] = [];
      try {
        const budgetData = await getBudgetData();
        if (budgetData?.monthlyData?.[currentMonth]?.employees) {
          employees = budgetData.monthlyData[currentMonth].employees;
        } else {
          // 월별 데이터가 없는 경우 빈 데이터로 시작
          employees = [];
        }
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '직원 데이터 로드 실패:', error);
        employees = [];
      }
      
      // 파일명에서 날짜 정보 추출 시도 (엑셀 파일명이 'YYYY-MM-DD진료비통계.xlsx' 형식인 경우)
      let extractedDate = formatDate(currentDate);
      debugLog('EXCEL_UPLOAD', '📅 [엑셀 업로드] 파일명에서 날짜 추출 시도:', uploadedFile.name);
      
      const fileNameDateMatch = uploadedFile.name.match(/(\d{4}-\d{2}-\d{2})진료비통계\.xlsx/);
      if (fileNameDateMatch && fileNameDateMatch[1]) {
        // 파일명에서 추출한 날짜를 현재 날짜로 설정
        extractedDate = fileNameDateMatch[1];
        debugLog('EXCEL_UPLOAD', '✅ [엑셀 업로드] 파일명에서 날짜 추출 성공:', extractedDate);
        setCurrentDate(new Date(extractedDate));
      } else {
        debugLog('EXCEL_UPLOAD', '⚠️ [엑셀 업로드] 파일명에서 날짜 추출 실패, 현재 날짜 사용:', extractedDate);
      }
          
          // 날짜별로 데이터 분리
          debugLog('EXCEL_UPLOAD', '📊 [엑셀 업로드] 날짜별 데이터 분리 시작');
          const dataByDate: Record<string, any> = {};
          
          // receiptItems를 날짜별로 그룹화
          debugLog('EXCEL_UPLOAD', '🧾 [엑셀 업로드] receiptItems 날짜별 그룹화 시작, 총 항목:', processedData.receiptData.items.length);
          processedData.receiptData.items.forEach(item => {
            const date = item.receiptDate;
            if (!dataByDate[date]) {
              dataByDate[date] = {
                receiptItems: [],
                assistReceiptItems: [],
                assistCashierDoctorItems: [],  // 🆕 수납자+진료의사 조합 데이터
                referralReceiptItems: [],
                totalReceipt: 0
              };
            }
            dataByDate[date].receiptItems.push(item);
            dataByDate[date].totalReceipt += item.amount;
          });
          
          // assistReceiptItems를 날짜별로 그룹화
          processedData.assistReceiptData.forEach(item => {
            const date = item.receiptDate;
            if (!dataByDate[date]) {
              dataByDate[date] = {
                receiptItems: [],
                assistReceiptItems: [],
                assistCashierDoctorItems: [],  // 🆕 수납자+진료의사 조합 데이터
                referralReceiptItems: [],
                totalReceipt: 0
              };
            }
            dataByDate[date].assistReceiptItems.push(item);
          });
          
          // 🆕 assistCashierDoctorItems를 날짜별로 그룹화
          processedData.assistCashierDoctorData.forEach(item => {
            const date = item.receiptDate;
            if (!dataByDate[date]) {
              dataByDate[date] = {
                receiptItems: [],
                assistReceiptItems: [],
                assistCashierDoctorItems: [],
                referralReceiptItems: [],
                totalReceipt: 0
              };
            }
            dataByDate[date].assistCashierDoctorItems.push(item);
          });
          
          // referralReceiptItems를 날짜별로 그룹화
          processedData.referralReceiptData.forEach(item => {
            const date = item.receiptDate;
            if (!dataByDate[date]) {
              dataByDate[date] = {
                receiptItems: [],
                assistReceiptItems: [],
                assistCashierDoctorItems: [],  // 🆕 수납자+진료의사 조합 데이터
                referralReceiptItems: [],
                totalReceipt: 0
              };
            }
            dataByDate[date].referralReceiptItems.push(item);
          });
          
          // 현재 화면에 표시할 날짜의 데이터 설정 (extractedDate 사용)
          if (dataByDate[extractedDate]) {
            setReceiptData({
              total: dataByDate[extractedDate].totalReceipt,
              items: dataByDate[extractedDate].receiptItems
            });
            debugLog('EXCEL_UPLOAD', '[엑셀 업로드] assistReceiptItems 설정:', dataByDate[extractedDate].assistReceiptItems);
            setAssistReceiptData(dataByDate[extractedDate].assistReceiptItems);
            debugLog('EXCEL_UPLOAD', '[엑셀 업로드] assistCashierDoctorItems 설정:', dataByDate[extractedDate].assistCashierDoctorItems);  // 🆕
            debugLog('EXCEL_UPLOAD', '[엑셀 업로드] referralReceiptItems 설정:', dataByDate[extractedDate].referralReceiptItems);
            setReferralReceiptData(dataByDate[extractedDate].referralReceiptItems);
          } else {
            // 현재 날짜에 데이터가 없는 경우 빈 데이터 설정
            setReceiptData({ total: 0, items: [] });
            setAssistReceiptData([]);
            setReferralReceiptData([]);
          }
          
          // setReportStatusData(processedData.reportStatusData); // 현재 사용하지 않음
          setValidationErrors(processedData.validationErrors);
          
          // 팀별 수납 현황 데이터 생성 (파일 업로드 후) - 사용되지 않아 주석 처리
          // const teamCollectionData = (() => {
          //   const filteredItems = processedData.assistReceiptData.filter(item => 
          //     item.receiptDate === extractedDate
          //   );

          //   // 팀별 데이터 집계 (어시스트명을 그대로 사용)
          //   const teamData = filteredItems.reduce((acc, item) => {
          //     // 여기서는 어시스트명을 팀명으로 사용
          //     const teamName = item.assistName || '미지정';
              
          //     if (!acc[teamName]) {
          //       acc[teamName] = { count: 0, amount: 0, assistants: [] };
          //     }
              
          //     acc[teamName].count += item.count;
          //     acc[teamName].amount += item.amount;
              
          //     // 어시스트별 중복 방지
          //     const existingAssist = acc[teamName].assistants.find(a => a.name === item.assistName);
          //     if (existingAssist) {
          //       existingAssist.count += item.count;
          //       existingAssist.amount += item.amount;
          //     } else {
          //       acc[teamName].assistants.push({
          //         name: item.assistName,
          //         count: item.count,
          //         amount: item.amount
          //       });
          //     }
              
          //     return acc;
          //   }, {} as Record<string, { count: number; amount: number; assistants: any[] }>);

          //   return {
          //     teamData,
          //     assistTeamMapping: {}, // 빈 객체로 설정
          //     totalCount: filteredItems.reduce((sum, item) => sum + item.count, 0),
          //     totalAmount: filteredItems.reduce((sum, item) => sum + item.amount, 0)
          //   };
          // })();

          // Firebase에 날짜별로 분리하여 저장
          const dateCount = Object.keys(dataByDate).length;
          debugLog('EXCEL_UPLOAD', `${dateCount}개 날짜의 데이터를 저장합니다:`, Object.keys(dataByDate));
          
          // 날짜별로 저장 Promise 배열 생성
          // 축소 관문에 걸려 보류된 날짜 (아래에서 한 번만 확인 요청)
          const shrinkHeld: Array<{ date: string; verdict: ShrinkVerdict; payload: any }> = [];
          const savePromises = Object.entries(dataByDate).map(async ([date, data]) => {
            // 해당 날짜의 팀별 수납 현황 데이터 생성 (서비스 사용)
            const generateTeamCollectionForDate = async (dateStr: string, assistItems: any[], currentEmployees: any[], currentBudgetData: any) => {
              const teamCollectionService = new TeamCollectionService();
              
              // 캐시어 매핑이 적용된 데이터 준비
              const sourceData = assistItems;
              
              // 월별 데이터 로드
              const yearMonth = dateStr.substring(0, 7);
              const monthlyData = await teamCollectionService.loadMonthlyData(yearMonth);
              
              // 어시스트별 월별 수납액 및 지출액 캐시 계산
              const monthlyReceiptCache: Record<string, number> = {};
              const monthlyExpenseCache: Record<string, number> = {};
              // ✅ 행 표시/저장용 월누계는 이름+팀 복합키로 구분 — 두 팀 소속 어시스트의 월누계가 양쪽 행에 이중 기입되는 것 방지
              //    (이름 단독 키 캐시는 calculateForDisplay의 월별 분배 로직이 그대로 사용하므로 유지)
              const monthlyReceiptByAssistTeam: Record<string, number> = {};
              const monthlyExpenseByAssistTeam: Record<string, number> = {};
              // ✅ 복합키 미스 폴백 판정용: 어시스트가 월별 데이터에서 등장한 팀 버킷 집합
              //    (단일 팀 버킷일 때만 이름 단독 캐시 폴백 허용 — 다중 팀이면 이중 기입 위험으로 0 유지)
              const monthlyTeamsByAssist: Record<string, Set<string>> = {};

              // 수납 데이터에서 어시스트별 월별 합계 계산
              if (monthlyData?.receipt && typeof monthlyData.receipt === 'object') {
                Object.entries(monthlyData.receipt).forEach(([teamName, teamData]) => {
                  if (Array.isArray(teamData)) {
                    teamData.forEach((item: any) => {
                      if (item && typeof item === 'object' && item.assistName) {
                        const assistName = item.assistName;
                        const amount = Number(item.amount) || 0;
                        monthlyReceiptCache[assistName] = (monthlyReceiptCache[assistName] || 0) + amount;
                        const receiptTeamKey = `${assistName}-${teamName}`;
                        monthlyReceiptByAssistTeam[receiptTeamKey] = (monthlyReceiptByAssistTeam[receiptTeamKey] || 0) + amount;
                        if (!monthlyTeamsByAssist[assistName]) monthlyTeamsByAssist[assistName] = new Set();
                        monthlyTeamsByAssist[assistName].add(teamName);
                      }
                    });
                  }
                });
              }

              // 지출 데이터에서 어시스트별 월별 합계 계산
              if (monthlyData?.expense && typeof monthlyData.expense === 'object') {
                Object.entries(monthlyData.expense).forEach(([teamName, teamData]) => {
                  if (Array.isArray(teamData)) {
                    teamData.forEach((item: any) => {
                      if (item && typeof item === 'object' && item.assistName) {
                        const assistName = item.assistName;
                        const amount = Number(item.amount) || 0;
                        monthlyExpenseCache[assistName] = (monthlyExpenseCache[assistName] || 0) + amount;
                        const expenseTeamKey = `${assistName}-${teamName}`;
                        monthlyExpenseByAssistTeam[expenseTeamKey] = (monthlyExpenseByAssistTeam[expenseTeamKey] || 0) + amount;
                        if (!monthlyTeamsByAssist[assistName]) monthlyTeamsByAssist[assistName] = new Set();
                        monthlyTeamsByAssist[assistName].add(teamName);
                      }
                    });
                  }
                });
              }
              
              // 캐시어 매핑을 위한 assistCashierDoctorData 생성
              const assistCashierDoctorData: any = {};
              sourceData.forEach(item => {
                if (item.assistName && item.cashierName) {
                  const key = `${item.assistName}_${item.cashierName}`;
                  if (!assistCashierDoctorData[key]) {
                    // 캐시어 매핑을 통한 진료의사 찾기
                    const cashierEmployee = teamCollectionService.findMatchingEmployee(item.cashierName, currentEmployees);
                    if (cashierEmployee) {
                      assistCashierDoctorData[key] = {
                        matchedDoctor: cashierEmployee.name,
                        team: cashierEmployee.team
                      };
                    }
                  }
                }
              });
              
              // calculateForDisplay 호출
              const { teamCollection, teamSummary } = await teamCollectionService.calculateForDisplay({
                assistItems: sourceData,
                employees: currentEmployees,
                budgetData: currentBudgetData,
                assistCashierDoctorData: assistCashierDoctorData,
                monthlyReceiptData: monthlyReceiptCache,
                monthlyExpenseData: monthlyExpenseCache,
                yearMonth: dateStr.substring(0, 7) // 🆕 진료의사 매핑을 위한 년월 정보 추가
              });
              
              // calculateForDisplay 결과를 배열 형태로 변환
              const teamCollectionArray: any[] = [];
              // let totalExpense = 0;
              const processedAssists = new Set<string>();

              // 🔒 오늘 표시에서 어시스트가 등장하는 팀 집합 — 복합키 미스 폴백의 이중 기입 차단용.
              //    (월 버킷이 단일이어도 오늘 두 팀 행이 생기면 양쪽 행이 같은 이름 단독 캐시를
              //     받아 이중 계상되므로, 오늘도 단일 팀일 때만 폴백을 허용한다)
              const todayTeamsByAssist: Record<string, Set<string>> = {};
              Object.keys(teamCollection).forEach(tn => {
                (teamCollection[tn]?.receiptItems || []).forEach((it: any) => {
                  const oa = it.assistName || 'unknown';
                  (todayTeamsByAssist[oa] = todayTeamsByAssist[oa] || new Set()).add(tn);
                });
              });

              // 팀별로 데이터 생성
              Object.keys(teamCollection).sort().forEach(teamName => {
                const summary = teamSummary[teamName];
                const team = teamCollection[teamName];
                
                if (!summary) return;
                
                // 팀 헤더 행 생성 코드 제거됨
                
                // 캐시어 매핑 결과에 따라 어시스트명 변경하여 그룹화
                const assistGroupMap = new Map<string, {count: number, amount: number, originalAssist: string, cashierName?: string, mappedFrom?: string, realAssistName: string, doctorName?: string, doctorNameConflict?: boolean}>();
                if (team.receiptItems) {
                  for (const item of team.receiptItems) {
                    // 캐시어 매핑을 통해 실제 어시스트 이름 결정
                    let realAssistName = item.assistName;
                    let mappedFrom: string | undefined = undefined;
                    
                    // 캐시어 매핑 확인 - 어시스트로 매칭 안되면 캐시어로 매칭 시도
                    const assistMatch = teamCollectionService.findMatchingEmployee(item.assistName, currentEmployees);
                    if (!assistMatch && item.cashierName) {
                      const cashierMatch = teamCollectionService.findMatchingEmployee(item.cashierName, currentEmployees);
                      if (cashierMatch && cashierMatch.team === teamName) {
                        // 캐시어 매핑이 성공한 경우 캐시어명을 실제 어시스트명으로 사용
                        realAssistName = item.cashierName;
                        mappedFrom = item.assistName;
                      }
                    }
                    
                    // 복합키: originalAssistName + team 조합
                    const compositeKey = `${item.assistName || 'unknown'}-${teamName}`;
                    
                    // 🆕 복합키 생성 확인 디버그 로그 (엑셀 업로드용)
                    debugLog('FIREBASE_SAVE', `🔍 [엑셀업로드 compositeKey 생성] ${compositeKey}: ${item.assistName} → ${teamName}`);
                    
                    const existing = assistGroupMap.get(compositeKey) || {
                    count: 0,
                    amount: 0,
                      originalAssist: item.assistName || '',
                      cashierName: item.cashierName || '',
                      mappedFrom: mappedFrom || '',
                      realAssistName: realAssistName,
                      doctorName: item.doctorName || '',
                      doctorNameConflict: false
                    };
                    // 🩺 이름 상이 병합 감지: 한 그룹에 복수 진료의사가 섞이면 doctorName을 비워
                    //    사후 doctorTeamMapping 등록 시 합산 전액이 한 팀으로 오귀속되는 것을 차단
                    const incomingDoctorName = item.doctorName || '';
                    const hasDoctorConflict = !!existing.doctorNameConflict ||
                      !!(incomingDoctorName && existing.doctorName && incomingDoctorName !== existing.doctorName);
                    if (hasDoctorConflict && !existing.doctorNameConflict) {
                      console.warn(`[엑셀업로드 assistGroup] ⚠️ ${compositeKey} 병합 중 진료의사 상이("${existing.doctorName}" ≠ "${incomingDoctorName}") — doctorName 비움`);
                    }
                    assistGroupMap.set(compositeKey, {
                      count: existing.count + (item.count || 0),
                      amount: existing.amount + (item.amount || 0),
                      originalAssist: item.assistName || existing.originalAssist || '',
                      cashierName: item.cashierName || existing.cashierName || '',
                      mappedFrom: mappedFrom || existing.mappedFrom || '',
                      realAssistName: realAssistName,
                      // 🩺 진료의사 정보 보존 — 집계 시점의 귀속 규칙(그날/그 달 의사 비율·의사 월 매핑)이 이 doctorName 을 쓴다
                      doctorName: hasDoctorConflict ? '' : (incomingDoctorName || existing.doctorName || ''),
                      doctorNameConflict: hasDoctorConflict
                    });
                  }
                }
                
                // 각 originalAssist + team 조합별로 개별 행 생성
                assistGroupMap.forEach((assistData, compositeKey) => {
                  // 🆕 복합키 처리 확인 디버그 로그 (엑셀 업로드용)
                  debugLog('FIREBASE_SAVE', `🔍 [엑셀업로드 compositeKey 처리] ${compositeKey}: ${assistData.realAssistName} → ${assistData.originalAssist}`);
                  
                  // 복합키에서 originalAssistName과 teamName 추출
                  
                  // 실제 어시스트명으로 직원 매칭
                  const matchedEmployee = teamCollectionService.findMatchingEmployee(assistData.realAssistName, currentEmployees);
                  
                  // 월별 데이터 계산 - 원본 어시스트명+팀 복합키 사용 (두 팀 소속 어시스트 월누계 이중 기입 방지)
                  const assistTeamCacheKey = `${assistData.originalAssist}-${teamName}`;
                  let assistMonthlyReceipt = monthlyReceiptByAssistTeam[assistTeamCacheKey] || 0;
                  let assistMonthlyExpense = monthlyExpenseByAssistTeam[assistTeamCacheKey] || 0;
                  // ✅ 복합키 미스 폴백: 월중 의사매핑 등록·팀 이동 등으로 팀명이 어긋나 복합키가 비면
                  //    해당 어시스트가 월별 데이터에 단일 팀 버킷으로만 존재할 때만 이름 단독 캐시로 폴백
                  //    (다중 팀 버킷이면 어느 팀 몫인지 판별 불가 → 이중 기입 방지 위해 0 유지 + 경고)
                  if (!(assistTeamCacheKey in monthlyReceiptByAssistTeam) && !(assistTeamCacheKey in monthlyExpenseByAssistTeam)) {
                    const assistMonthlyTeams = monthlyTeamsByAssist[assistData.originalAssist];
                    const assistTodayTeams = todayTeamsByAssist[assistData.originalAssist];
                    // 폴백 조건: 월 버킷 단일 && 오늘 표시도 단일 팀 — 오늘 두 팀 행이면 양쪽이 같은
                    // 총액을 받아 이중 계상되므로(전이일 창) 0 유지가 안전 (다음 업로드에서 자가 수리)
                    if (assistMonthlyTeams && assistMonthlyTeams.size === 1 && (!assistTodayTeams || assistTodayTeams.size === 1)) {
                      assistMonthlyReceipt = monthlyReceiptCache[assistData.originalAssist] || 0;
                      assistMonthlyExpense = monthlyExpenseCache[assistData.originalAssist] || 0;
                    } else if (assistMonthlyTeams && assistMonthlyTeams.size >= 1) {
                      console.warn(`[엑셀업로드 월누계] ⚠️ 복합키 미스 "${assistTeamCacheKey}" — 월 버킷(${Array.from(assistMonthlyTeams).join(', ')})·오늘 팀(${assistTodayTeams ? Array.from(assistTodayTeams).join(', ') : '-'}) 다중/불일치라 폴백 없이 0 유지`);
                    }
                  }
                  const assistMonthlyProfit = assistMonthlyReceipt - assistMonthlyExpense;
                  
                  const assistRow: any = {
                    team: teamName,
                    teamName: '',
                    assistName: assistData.realAssistName,
                    compositeKey: compositeKey,
                    count: assistData.count || 0,
                    amount: assistData.amount || 0,
                    monthlyReceipt: assistMonthlyReceipt,
                    monthlyExpense: assistMonthlyExpense,
                    monthlyProfit: assistMonthlyProfit,
                    receiptTotal: assistMonthlyReceipt,
                    expenseTotal: assistMonthlyExpense,
                    isAssistRow: true,
                    isUnassignedEmployee: !matchedEmployee
                  };
                  
                  // undefined가 아닌 값들만 추가 (Firebase 호환성)
                  if (assistData.originalAssist && assistData.originalAssist.trim()) {
                    assistRow.originalAssistName = assistData.originalAssist;
                  }
                  if (assistData.cashierName && assistData.cashierName.trim()) {
                    assistRow.cashierName = assistData.cashierName;
                  }
                  if (assistData.mappedFrom && assistData.mappedFrom.trim()) {
                    assistRow.isCashierMapped = true;
                    assistRow.mappedFrom = assistData.mappedFrom;
                  } else {
                    assistRow.isCashierMapped = false;
                  }
                  // 🩺 진료의사 정보 저장 — 미지정 행의 의사 기반 팀 귀속(salesAttribution 정본)이 이 값을 쓴다
                  if (assistData.doctorName && assistData.doctorName.trim()) {
                    assistRow.doctorName = assistData.doctorName;
                  }
                  
                  teamCollectionArray.push(assistRow);
                  
                  processedAssists.add(compositeKey);
                });
                
                // 팀 헤더 행 업데이트 관련 코드 모두 제거됨
                
              });
              
              // Firebase 저장 전 undefined 값 정리
              const cleanedArray = teamCollectionArray.map(item => {
                const cleanedItem: any = {};
                Object.keys(item).forEach(key => {
                  if (item[key] !== undefined && item[key] !== null) {
                    cleanedItem[key] = item[key];
                  }
                });
                return cleanedItem;
              });
              
              return cleanedArray;
            };
            
            const dateTeamCollectionData = await generateTeamCollectionForDate(date, data.assistCashierDoctorItems || data.assistReceiptItems, employees, budgetData);
            
            // 해당 날짜의 일계표 현황 계산
            const dateReceiptItems = data.receiptItems;
            
            // 각 수납 항목별 합계 계산
            const dateCashReceipt = dateReceiptItems.reduce((sum: number, item: any) => sum + (item.cashAmount || 0), 0);
            const dateCardReceipt = dateReceiptItems.reduce((sum: number, item: any) => sum + (item.cardAmount || 0), 0);
            const dateTransferReceipt = dateReceiptItems.reduce((sum: number, item: any) => sum + (item.transferAmount || 0), 0);
            const dateInsuranceClaim = dateReceiptItems.reduce((sum: number, item: any) => sum + (item.insuranceClaim || 0), 0);
            const dateHealthLifeSupport = dateReceiptItems.reduce((sum: number, item: any) => sum + (item.healthLifeSupport || 0), 0);
            
            // 🎯 총수납 계산 (실제 현금 흐름만 - 미수금 제외)
            const dateTotalReceipt = dateCashReceipt + dateCardReceipt + dateTransferReceipt + 
                                   dateInsuranceClaim + dateHealthLifeSupport;
            
            // 전일 수납액 계산 (현재 날짜 이전의 가장 최근 데이터)
            let datePrevDayTotal = 0;
            const allDates = Object.keys(dataByDate).sort();
            const currentDateIndex = allDates.indexOf(date);
            if (currentDateIndex > 0) {
              const prevDateData = dataByDate[allDates[currentDateIndex - 1]];
              if (prevDateData) {
                datePrevDayTotal = prevDateData.totalReceipt;
              }
            }
            
            // Firebase에서 해당 월의 모든 데이터를 가져와서 월 총매출액 계산
            const dateMonth = date.substring(0, 7);
            let dateMonthlyTotal = 0;
            
            // 먼저 현재 엑셀의 해당 월 데이터를 계산
            Object.entries(dataByDate).forEach(([d, data]) => {
              if (d.substring(0, 7) === dateMonth && d <= date) {
                dateMonthlyTotal += data.totalReceipt;
              }
            });
            
            // Firebase에서 기존 데이터도 가져와서 합산 (비동기 처리를 위해 나중에 처리)
            
            // 부가가치세 합계 계산
            let dateTotalVAT = 0;
            let vatItems: Array<{index: number, name: string, chartNumber: string, vat: number}> = [];
            debugLog('VAT_CALCULATION', `🧮 [Excel VAT 디버깅] ${date} 부가가치세 계산 시작 (총 ${data.receiptItems.length}개 항목)`);
            
            data.receiptItems.forEach((item: any, index: number) => {
              const itemVAT = item.valueAdded || 0;
              if (itemVAT && typeof itemVAT === 'number' && itemVAT > 0) {
                dateTotalVAT += itemVAT;
                vatItems.push({
                  index,
                  name: item.name || '이름없음',
                  chartNumber: item.chartNumber || '차트번호없음',
                  vat: itemVAT
                });
              }
            });
            
            // 부가가치세 디버깅 로그
            debugLog('VAT_CALCULATION', `🧮 [Excel VAT 디버깅] ${date} 부가가치세 계산 완료:`, {
              totalItems: data.receiptItems.length,
              vatItemsCount: vatItems.length,
              vatItems: vatItems,
              totalVAT: dateTotalVAT.toLocaleString() + '원'
            });
            
            if (dateTotalVAT > 0) {
              debugLog('VAT_CALCULATION', `[부가가치세] ${date} 날짜의 부가가치세 총액: ${dateTotalVAT.toLocaleString()}원`);
            } else {
                              debugLog('VAT_CALCULATION', `🧮 [Excel VAT 디버깅] ${date} 부가가치세가 0원입니다. valueAdded 필드 확인 필요`);
            }
            
            const dateReportStatus = [{
              '구분': '일계표현황',
              '총수납': dateTotalReceipt,
              '전일총수납액': datePrevDayTotal,
              '월총매출액': dateMonthlyTotal,
              '지출합계': 0, // 엑셀 업로드 시점에는 지출 데이터 없음
              '월총부가가치세': dateTotalVAT, // 🧮 부가가치세 필드명 통일
              '부가가치세': dateTotalVAT, // 🧮 추가 호환성을 위한 필드
              'VAT': dateTotalVAT, // 🧮 추가 호환성을 위한 필드
              '부가세': dateTotalVAT, // 🧮 추가 호환성을 위한 필드
              '메모': `${date} 일계표`,
              date: date
            }];
            
            // Excel 업로드 시 팀별 수납 현황 데이터 검증 로그
                          debugLog('EXCEL_UPLOAD', `📊 [Excel 업로드 Firebase 저장] ${date} 팀별 수납 현황 저장 데이터:`, {
              totalRows: dateTeamCollectionData.length,
              teamRows: 0, // 팀 행 제거됨
              unassignedRows: dateTeamCollectionData.filter(item => item.isUnassignedEmployee).length,
              detailedData: dateTeamCollectionData.map(item => ({
                teamName: item.teamName,
                assistName: item.assistName,
                compositeKey: item.compositeKey, // 🆕 복합키 정보 추가
                originalAssistName: item.originalAssistName,
                amount: item.amount,
                // isTeamRow 필드 제거됨
                isUnassigned: item.isUnassignedEmployee
              }))
            });
            
            // 엑셀 업로드 시에도 빈 file 객체 제거 적용
            const dataToSave = removeEmptyFileObjects({
              receipts: {
                total: data.totalReceipt,
                items: data.receiptItems
              },
              assistReceipts: data.assistReceiptItems,
              assistCashierDoctorReceipts: data.assistCashierDoctorItems,  // 🆕 수납자+진료의사 조합 데이터
              referralReceipts: data.referralReceiptItems,
              reportStatus: dateReportStatus,
              // 💸 지출은 **보내지 않는다** — saveDailyReportData 가 merge:true 라 기존 값이 보존된다.
              //    엑셀 처리기(ExcelProcessorResult)에는 지출 필드가 아예 없으므로 업로드가 쓸 지출이
              //    없는데도 { total:0, items:[] } 를 보내 그날 지출을 매번 지우고 있었다.
              teamCollection: dateTeamCollectionData,
              file: {
                name: uploadedFile.name,
                uploadTime: timeString
              }
            });

            // 🛡️ 축소 관문 — 이 저장이 그날 전표를 크게 지우면 보류하고 목록에 담는다.
            //    업로드는 파일 종류를 구분하지 않고 파일이 언급한 모든 진료일 문서를 갈아끼운다.
            //    7월에 올린 수납통계가 6월 문서를 열어 27장 14,959,980원을 1장 0원으로 만든 사고
            //    (일곱 달 223,197,180원 소실)를 여기서 막는다.
            return (async () => {
              const existing = await getDailyReportData(date).catch(() => null);
              const verdict = assessDailyShrink(
                date,
                summarizeReceipts((existing as any)?.receipts?.items),
                summarizeReceipts(dataToSave?.receipts?.items)
              );
              if (verdict.blocked) {
                shrinkHeld.push({ date, verdict, payload: dataToSave });
                return;
              }
              return saveDailyReportData(dataToSave, date);
            })();
          });
          
          // 모든 날짜의 데이터 저장을 명시적으로 대기
          try {
            debugLog('EXCEL_UPLOAD', '🔄 [엑셀 업로드] Firebase 저장 시작...');
            await Promise.all(savePromises);
            debugLog('EXCEL_UPLOAD', '✅ [엑셀 업로드] Firebase 저장 완료');

            // 🛡️ 보류된 날짜를 **한 번만** 묻는다. 날짜마다 확인창이 뜨면 쓸 수 없다.
            //    취소하면 그 날짜들만 건너뛰고 나머지는 이미 저장된 상태로 계속 간다.
            let shrinkNotice = '';
            if (shrinkHeld.length > 0) {
              const heldDates = shrinkHeld.map((h) => h.date);
              const proceed = window.confirm(
                buildShrinkConfirmMessage(shrinkHeld.map((h) => h.verdict), heldDates)
              );
              if (proceed) {
                await Promise.all(shrinkHeld.map((h) => saveDailyReportData(h.payload, h.date)));
                shrinkNotice =
                  `⚠️ 전표가 크게 줄어드는 ${heldDates.length}개 날짜를 사용자 확인 후 덮어썼습니다.\n` +
                  shrinkHeld.map((h) => `   · ${h.verdict.summary}`).join('\n');
              } else {
                shrinkNotice =
                  `🛡️ 전표가 크게 줄어드는 ${heldDates.length}개 날짜는 저장하지 않았습니다 (기존 명세 유지).\n` +
                  shrinkHeld.map((h) => `   · ${h.verdict.summary}`).join('\n') +
                  `\n   올린 파일이 그 날짜를 담당하는 파일인지 확인해 주세요.`;
              }
              debugWarn('EXCEL_UPLOAD', `🛡️ [축소 관문] ${heldDates.join(', ')} — ${proceed ? '사용자 승인 후 저장' : '저장 보류'}`);
            }
            
            // 저장된 월들의 월총매출액 재계산
            const monthsToUpdate = new Set<string>();
            Object.keys(dataByDate).forEach(date => {
              monthsToUpdate.add(date.substring(0, 7));
            });
            
            // 각 월별로 월총매출액 업데이트
            for (const yearMonth of monthsToUpdate) {
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[엑셀 업로드] ${yearMonth}월 월총매출액 업데이트 시작`);
              const monthlyTotal = await calculateMonthlyTotal(yearMonth);
              await updateAllReportStatusWithMonthlyTotal(yearMonth, monthlyTotal);
            }
            
            // 현재 화면의 월총매출액도 업데이트
            await updateMonthlyTotalAfterUpload();
            
            // 🆕 엑셀 업로드 후 자동으로 메인 저장 함수 호출 (teamCollection 데이터 생성)
            debugLog('EXCEL_UPLOAD', '🔄 [엑셀 업로드] 자동 저장 함수 호출 시작 (teamCollection 생성)...');
            try {
              // 🛡️ 디플레이션 게이트 경고(deflationNotice)를 받아 업로드 결과 alert에 병기
              // 📅 집계 대상 월을 **명시**한다 — handleSave 안에서 currentDate 로 유추하면
              //    방금 부른 setCurrentDate 가 아직 반영되지 않아 틀린 달을 집계한다.
              const autoSaveOutcome = await handleSave({ interactive: false, yearMonths: Array.from(monthsToUpdate) });
              debugLog('EXCEL_UPLOAD', '✅ [엑셀 업로드] 자동 저장 완료 (teamCollection 생성 완료)');
              
              // 📌 teamCollection 생성 완료 후 잠시 대기 (Firebase 반영 시간 확보)
              // debugLog('EXCEL_UPLOAD', '⏳ [엑셀 업로드] teamCollection 반영 대기 중 (1초)...');
              // await new Promise(resolve => setTimeout(resolve, 1000));
              
              // 🆕 teamCollection 생성 후 실제매출 자동화 새로고침 트리거
              // 🛡️ 디플레이션 게이트가 월별 집계 저장을 보류(declined)한 경우: 하류 새로고침·웹훅은
              // 구 기준선을 재생산할 뿐 수축값 전파는 없으나, 방어적으로 체인 전체를 건너뛴다.
              if (autoSaveOutcome?.declined) {
                debugWarn('EXCEL_UPLOAD', '⚠️ [엑셀 업로드] 월별 집계 저장 보류 — 실제매출/팀성과 새로고침·웹훅 체인을 건너뜁니다.');
              } else try {
                const uploadedMonth = extractedDate.substring(0, 7); // YYYY-MM 형식으로 변환
                debugLog('EXCEL_UPLOAD', `🔄 [엑셀 업로드] 실제매출 자동화 새로고침 트리거 시작 (${uploadedMonth}월)...`);
                await triggerActualSalesRefreshAfterExcelUpload(uploadedMonth);
                debugLog('EXCEL_UPLOAD', '✅ [엑셀 업로드] 실제매출 자동화 새로고침 완료');
                
                // 📌 실제매출 데이터 Firebase 저장 완료 대기 (추가 2초)
                // debugLog('EXCEL_UPLOAD', '⏳ [엑셀 업로드] 실제매출 데이터 Firebase 저장 완료 대기 중 (2초)...');
                // await new Promise(resolve => setTimeout(resolve, 2000));
                
                // 🆕 실제매출 자동화 새로고침 후 팀 성과 관리 새로고침 트리거
                debugLog('EXCEL_UPLOAD', `🔄 [엑셀 업로드] 팀 성과 관리 새로고침 트리거 시작 (${uploadedMonth}월)...`);
                
                // 필요한 데이터를 AppContext에서 가져오기
                const currentTeams = teams || [];
                const currentEmployees = employees || [];
                
                if (currentTeams.length === 0 || currentEmployees.length === 0 || !budgetData) {
                  DailyReportDebugLogger.error('ERROR_HANDLING', `❌ [엑셀 업로드] 팀 성과 관리 새로고침 불가 - 필수 데이터 없음 (팀: ${currentTeams.length}개, 직원: ${currentEmployees.length}개, 예산데이터: ${budgetData ? '있음' : '없음'})`);
                } else {
                  const targetDate = new Date(uploadedMonth + '-01');
                  const refreshResult = await TeamPerformanceRefreshService.refreshTeamPerformanceData(
                    targetDate,
                    currentTeams,
                    currentEmployees,
                    budgetData,
                    () => {}, // setBudgetData - DailyReportPage에서는 업데이트 함수가 없으므로 빈 함수
                    {
                      forceRefresh: false, // 캐시 데이터가 있으면 사용
                      saveData: true,      // 결과를 Firebase에 저장
                      showLogs: false      // 콘솔 로그 출력 안함 (디버그 로그로 대체)
                    }
                  );
                  
                  if (refreshResult.success && refreshResult.suspectMisattribution) {
                    // 🛡️ 오귀속 의심 결과는 외부 전송 금지 — 성과 저장과 동일 게이트(performRefresh) 공유.
                    // 매핑 복구 후 다시 업로드/새로고침하면 정상 전송된다.
                    debugWarn('EXCEL_UPLOAD', '⚠️ [엑셀 업로드] 진료의사 매핑 부재 상태의 오귀속 의심 결과 — 웹훅 전송을 건너뜁니다.');
                  } else if (refreshResult.success) {
                    debugLog('EXCEL_UPLOAD', `✅ [엑셀 업로드] 팀 성과 관리 새로고침 완료 - 총 매출: ${refreshResult.totalActualSales.toLocaleString()}원, 팀 수: ${refreshResult.performanceData.length}개`);

                    // 🆕 팀 성과 데이터 웹훅 전송
                    try {
                      debugLog('EXCEL_UPLOAD', '🔄 [엑셀 업로드] 팀 성과 데이터 웹훅 전송 시작...');
                      
                      // 웹훅 설정 확인
                      const webhookConfigCheck = dailyReportWebhookService.checkWebhookConfig();
                      if (!webhookConfigCheck.isValid) {
                        debugWarn('EXCEL_UPLOAD', `⚠️ [엑셀 업로드] 웹훅 설정 미완료: ${webhookConfigCheck.message}`);
                      } else {
                        // 웹훅 전송
                        const webhookResult = await dailyReportWebhookService.sendTeamPerformanceWebhook(
                          refreshResult.performanceData,
                          {
                            uploadDate: new Date().toISOString(),
                            reportDate: extractedDate,
                            uploadedBy: user?.email || 'unknown',
                            fileName: file?.name || 'unknown_file'
                          }
                        );
                        
                        if (webhookResult.success) {
                          debugLog('EXCEL_UPLOAD', '✅ [엑셀 업로드] 팀 성과 데이터 웹훅 전송 성공');
                        } else {
                          debugWarn('EXCEL_UPLOAD', `⚠️ [엑셀 업로드] 웹훅 전송 실패: ${webhookResult.message}`);
                        }
                      }
                    } catch (webhookError) {
                      DailyReportDebugLogger.error('ERROR_HANDLING', '❌ [엑셀 업로드] 웹훅 전송 중 오류:', webhookError);
                      // 웹훅 실패해도 전체 프로세스는 성공으로 처리
                    }
                  } else {
                    DailyReportDebugLogger.error('ERROR_HANDLING', `❌ [엑셀 업로드] 팀 성과 관리 새로고침 실패: ${refreshResult.error}`);
                    throw new Error(refreshResult.error || '팀 성과 관리 새로고침 실패');
                  }
                }
              } catch (refreshError) {
                DailyReportDebugLogger.error('ERROR_HANDLING', '❌ [엑셀 업로드] 실제매출/팀성과 자동화 새로고침 실패:', refreshError);
                // 새로고침 실패해도 엑셀 업로드는 성공적으로 완료된 상태이므로 알림만 표시
              }
              
              // 부가가치세 총액 계산
              let totalVATSaved = 0;
              Object.values(dataByDate).forEach((data: any) => {
                data.receiptItems.forEach((item: any) => {
                  if (item.valueAdded && typeof item.valueAdded === 'number') {
                    totalVATSaved += item.valueAdded;
                  }
                });
              });
              
              // 성공 메시지 통합
              let successMessage = `🎉 엑셀 업로드, teamCollection 생성, 실제매출 자동화 새로고침 및 팀 성과 관리 새로고침이 모두 완료되었습니다!\n\n📅 저장된 날짜: ${Object.keys(dataByDate).join(', ')}\n📊 월별 집계 업데이트: ${Array.from(monthsToUpdate).join(', ')}월`;
              
              // 부가가치세 정보 추가
              if (totalVATSaved > 0) {
                successMessage += `\n💰 부가가치세: ${totalVATSaved.toLocaleString()}원 저장됨`;
              }

              // ⚠️ 어시스트 빈칸 안내 — 조용히 '미지정'으로 들어간 건수를 업로드 직후에 드러낸다.
              //    2026-08 재업로드 때 엑셀 '담당직원' 42칸이 비어 미지정으로 저장됐는데(2,723,360원),
              //    완료 알림에 아무 표시가 없어 실제매출 화면을 열어보기 전까지 아무도 몰랐다.
              //    빈칸이 0건이면 아무것도 붙지 않는다.
              const blankAssistItems = (processedData.receiptData?.items || []).filter(
                (item: any) => (String(item?.assistName ?? '').trim() || '미지정') === '미지정'
              );
              if (blankAssistItems.length > 0) {
                const blankAmount = blankAssistItems.reduce((sum: number, item: any) => sum + (Number(item?.amount) || 0), 0);
                const countByDate = blankAssistItems.reduce((acc: Record<string, number>, item: any) => {
                  const day = String(item?.receiptDate || '').slice(5) || '(날짜없음)';
                  acc[day] = (acc[day] || 0) + 1;
                  return acc;
                }, {} as Record<string, number>);
                const dateBreakdown = Object.keys(countByDate).sort().map(d => `${d}(${countByDate[d]})`).join(', ');
                successMessage += `\n\n⚠️ 어시스트 칸이 빈 수납건 ${blankAssistItems.length}건 (${blankAmount.toLocaleString()}원) → 미지정으로 들어갔습니다.`
                  + `\n   해당 날짜: ${dateBreakdown}`
                  + `\n   엑셀 '담당직원' 칸을 확인하시거나, 실제매출 화면에서 팀을 지정하세요.`;
              }
              
              // 🛡️ 축소 관문 결과 병기 — '조용한 실패'를 만들지 않는다(DESIGN.md).
              if (shrinkNotice) {
                successMessage += `\n\n${shrinkNotice}`;
              }

              // 현재 날짜가 extractedDate와 다르면 추가 안내
              if (formatDate(currentDate) !== extractedDate) {
                successMessage += `\n\n📍 화면이 ${extractedDate} 날짜로 이동되었습니다.`;
              }

              // 🛡️ 디플레이션 게이트 경고 병기 (자동 저장 경로 무음 통과 제거)
              if (autoSaveOutcome?.deflationNotice) {
                successMessage += `\n\n${autoSaveOutcome.deflationNotice}`;
              }
              // 🛡️ 저장 보류(declined) 시 하류 실제매출·팀성과 갱신·웹훅도 건너뛰었음을 결과 alert에 명시
              if (autoSaveOutcome?.declined) {
                successMessage += `\n실제매출·팀성과 갱신도 함께 보류했습니다. 원본 확인 후 수동 저장 시 함께 갱신됩니다.`;
              }

              alert(successMessage);
            } catch (saveError) {
              DailyReportDebugLogger.error('ERROR_HANDLING', '❌ [엑셀 업로드] teamCollection 생성 또는 자동화 새로고침 실패:', saveError);
              alert(`⚠️ 엑셀 업로드는 완료되었지만 teamCollection 생성 또는 자동화 새로고침 중 오류가 발생했습니다:\n\n${saveError.message || '알 수 없는 오류'}\n\n수동으로 저장 버튼을 눌러주세요.`);
              
              // 현재 날짜가 extractedDate와 다르면 알림
              if (formatDate(currentDate) !== extractedDate) {
                alert(`📍 화면이 ${extractedDate} 날짜로 이동되었습니다.`);
              }
            }
            
          } catch (firebaseError) {
            DailyReportDebugLogger.error('ERROR_HANDLING', 'Firebase 저장 중 오류:', firebaseError);
            alert(`데이터 저장 중 오류가 발생했습니다: ${firebaseError?.message || '알 수 없는 오류'}`);
          } finally {
            DailyReportDebugLogger.loadingState('END', 'handleSave 완료');
        setIsLoading(false);
          }

          // 🔧 엑셀 업로드 완료 시 플래그 해제
          setIsExcelUploading(false);
    } catch (error) {
      debugLog('EXCEL_UPLOAD', '❌ [엑셀 업로드] 파일 처리 중 오류 발생:', error);

      // 🔧 에러 발생 시 플래그 해제
      setIsExcelUploading(false);
              DailyReportDebugLogger.error('ERROR_HANDLING', '엑셀 파일 처리 중 오류 발생:', error);
      let errorMessage = '엑셀 파일을 처리하는 중 오류가 발생했습니다.';
      
      if (error instanceof Error) {
        errorMessage += ` 원인: ${error.message}`;
        debugLog('EXCEL_UPLOAD', '❌ [엑셀 업로드] 오류 상세:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
      
      alert(errorMessage);
      DailyReportDebugLogger.loadingState('END', '위치 6');
        setIsLoading(false);
    }
  };
  
  
  // TeamCollectionService 인스턴스 생성 (컴포넌트 전체에서 재사용) - 현재 사용되지 않음
  // const teamCollectionService = React.useMemo(() => new TeamCollectionService(), []);

  // Firebase에서 이전 날짜의 총수납액을 가져오는 함수
  const getPreviousDayTotal = async (currentDate: string): Promise<number> => {
    try {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[전일총수납액 계산] 현재 날짜: ${currentDate}`);
      
      // 현재 날짜 이전의 최근 30일간 데이터 조회
      const current = new Date(currentDate);
      let prevTotal = 0;
      let foundDate = '';
      
      // 최대 30일 전까지 확인
      for (let i = 1; i <= 30; i++) {
        const checkDate = new Date(current);
        checkDate.setDate(checkDate.getDate() - i);
        const dateStr = formatDate(checkDate);
        
        try {
          const reportData = await getDailyReportData(dateStr);
          if (reportData && reportData.reportStatus && reportData.reportStatus.length > 0) {
            const status = reportData.reportStatus[0] as any;
            if (status && status['총수납']) {
              prevTotal = status['총수납'];
              foundDate = dateStr;
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[전일총수납액] ${dateStr}의 총수납: ${prevTotal.toLocaleString()}원 (${i}일 전)`);
              break;
            }
          }
        } catch (error) {
          // 해당 날짜에 데이터가 없으면 계속 진행
          continue;
        }
      }
      
      if (!foundDate) {
        DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[전일총수납액] 최근 30일 내 데이터 없음');
      }
      
      return prevTotal;
    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '전일총수납액 조회 실패:', error);
      return 0;
    }
  };

  // 월별 총매출액을 Firebase에서 계산하는 함수 (지출 포함)
  const calculateMonthlyTotal = async (yearMonth: string): Promise<number> => {
    try {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 계산 시작] ${yearMonth} (지출 포함)`);
      
      // 수납 데이터와 지출 데이터를 병렬로 가져오기
      const [monthlyReceiptData, monthlyExpenseData] = await Promise.all([
        getMonthlyReceiptData(yearMonth),
        getMonthlyExpenseData(yearMonth)
      ]);
      
      // 수납 총액 계산
      const receiptTotal = monthlyReceiptData.reduce((sum: number, item: {date: string, amount: number}) => sum + (item.amount || 0), 0);
      
      // 지출 총액 계산
      const expenseTotal = monthlyExpenseData.reduce((sum: number, item: {date: string, amount: number}) => sum + (item.amount || 0), 0);
      
      // 월총매출액 = 수납총액 - 지출총액
      const monthlyTotal = receiptTotal - expenseTotal;
      
      // 디버깅 정보 출력
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 디버깅] ${yearMonth}월 수납 데이터: ${monthlyReceiptData.length}개`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 디버깅] ${yearMonth}월 지출 데이터: ${monthlyExpenseData.length}개`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 디버깅] 수납 총액: ${receiptTotal.toLocaleString()}원`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 디버깅] 지출 총액: ${expenseTotal.toLocaleString()}원`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 계산 완료] ${yearMonth}: ${monthlyTotal.toLocaleString()}원 (수납 - 지출)`);
      
      return monthlyTotal;
    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '월총매출액 계산 실패:', error);
      return 0;
    }
  };

  // 월별 부가가치세 총액 계산
  const calculateMonthlyVAT = async (yearMonth: string): Promise<number> => {
    try {
      debugLog('VAT_CALCULATION', `🧮 [DailyReport VAT 디버깅] ====== ${yearMonth} 월별 부가가치세 계산 시작 ======`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총부가가치세 계산 시작] ${yearMonth}`);
      
      // 부가가치세 데이터 가져오기
              debugLog('VAT_CALCULATION', `🧮 [DailyReport VAT 디버깅] getMonthlyVATData 호출 중...`);
      const monthlyVATData = await getMonthlyVATData(yearMonth);
              debugLog('VAT_CALCULATION', `🧮 [DailyReport VAT 디버깅] getMonthlyVATData 결과:`, monthlyVATData);
      
      // 부가가치세 총액 계산
      const vatTotal = monthlyVATData.reduce((sum: number, item: {date: string, amount: number}) => sum + (item.amount || 0), 0);
      
      // 디버깅 정보 출력
              debugLog('VAT_CALCULATION', `🧮 [DailyReport VAT 디버깅] 계산 상세:`, {
        yearMonth,
        dataCount: monthlyVATData.length,
        rawData: monthlyVATData,
        calculation: monthlyVATData.map(item => `${item.date}: ${(item.amount || 0).toLocaleString()}원`),
        totalVAT: vatTotal.toLocaleString() + '원'
      });
      
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총부가가치세 디버깅] ${yearMonth}월 부가가치세 데이터: ${monthlyVATData.length}개`);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총부가가치세 계산 완료] ${yearMonth}: ${vatTotal.toLocaleString()}원`);
      
              debugLog('VAT_CALCULATION', `🧮 [DailyReport VAT 디버깅] ====== ${yearMonth} 계산 완료: ${vatTotal.toLocaleString()}원 ======`);
      return vatTotal;
    } catch (error) {
              debugError('VAT_CALCULATION', '🧮 [DailyReport VAT 디버깅] 월총부가가치세 계산 실패:', error);
              DailyReportDebugLogger.error('ERROR_HANDLING', '월총부가가치세 계산 실패:', error);
      return 0;
    }
  };

  // 빈 file 객체 제거 유틸리티 함수
  const removeEmptyFileObjects = (data: any): any => {
    const cleanData = { ...data };
    
    // file 객체가 있고, name이 빈 문자열이거나 의미 없는 값인 경우 제거
    if (cleanData.file) {
      const fileName = cleanData.file.name || '';
      const isEmptyFile = !fileName || fileName.trim() === '' || fileName === 'undefined';
      
      if (isEmptyFile) {
        debugLog('FILE_OPTIMIZATION', '🗑️ [File 최적화] 빈 file 객체 제거됨:', cleanData.file);
        delete cleanData.file;
      } else {
                  debugLog('FILE_OPTIMIZATION', '✅ [File 최적화] 유효한 file 객체 유지:', cleanData.file);
      }
    }
    
    return cleanData;
  };

  // 기존 Firebase 데이터 정리 함수 (개발 모드에서만 사용)
  const cleanupExistingEmptyFiles = async (targetMonth: string) => {
    if (process.env.NODE_ENV !== 'development') {
      debugWarn('FILE_OPTIMIZATION', '⚠️ [File 최적화] 데이터 정리는 개발 모드에서만 실행됩니다.');
      return;
    }

    try {
      debugLog('FILE_OPTIMIZATION', `🧹 [File 최적화] ${targetMonth}월 기존 데이터 정리 시작...`);
      
      const year = parseInt(targetMonth.substring(0, 4));
      const month = parseInt(targetMonth.substring(5, 7));
      const lastDay = new Date(year, month, 0).getDate();
      
      let cleanedCount = 0;
      let totalChecked = 0;
      
      for (let day = 1; day <= lastDay; day++) {
        const date = `${targetMonth}-${String(day).padStart(2, '0')}`;
        
        try {
          const reportData = await getDailyReportData(date);
          if (reportData) {
            totalChecked++;
            
            // 빈 file 객체가 있는지 확인
            if (reportData.file) {
              const fileName = reportData.file.name || '';
              const isEmptyFile = !fileName || fileName.trim() === '' || fileName === 'undefined';
              
              if (isEmptyFile) {
                debugLog('FILE_OPTIMIZATION', `🗑️ [File 최적화] ${date} 빈 file 객체 발견, 제거 중...`);
                
                // 빈 file 객체 제거 후 다시 저장
                const cleanedData = removeEmptyFileObjects(reportData);
                await saveDailyReportData(cleanedData, date);
                cleanedCount++;
              }
            }
          }
        } catch (error) {
          // 해당 날짜에 데이터가 없으면 계속 진행
          continue;
        }
      }
      
              debugLog('FILE_OPTIMIZATION', `✅ [File 최적화] ${targetMonth}월 정리 완료: ${cleanedCount}개 문서 정리됨 (총 ${totalChecked}개 확인)`);
      
      if (cleanedCount > 0) {
        const savedSpace = cleanedCount * 0.05; // 각 빈 file 객체당 약 5% 절약 추정
                  debugLog('FILE_OPTIMIZATION', `💾 [File 최적화] 예상 절약 공간: 약 ${savedSpace.toFixed(1)}% (${cleanedCount}개 문서)`);
      }
      
    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '❌ [File 최적화] 기존 데이터 정리 중 오류:', error);
    }
  };

  // 모든 날짜의 reportStatus를 업데이트하는 함수
  const updateAllReportStatusWithMonthlyTotal = async (yearMonth: string, monthlyTotal: number) => {
    try {
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 업데이트] ${yearMonth}월의 모든 reportStatus 업데이트 시작`);
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 업데이트] 업데이트할 월총매출액: ${monthlyTotal.toLocaleString()}원`);
      
      // 해당 월의 모든 일자 가져오기
      const year = parseInt(yearMonth.substring(0, 4));
      const month = parseInt(yearMonth.substring(5, 7));
      const lastDay = new Date(year, month, 0).getDate();
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 업데이트] ${yearMonth}월의 일수: ${lastDay}일`);
      
      const updatePromises: Promise<void>[] = [];
      let updatedCount = 0;
      
      for (let day = 1; day <= lastDay; day++) {
        const date = `${yearMonth}-${String(day).padStart(2, '0')}`;
        const dailyReportRef = doc(db, 'dailyReports', date);
        
        updatePromises.push(
          getDoc(dailyReportRef).then(async (docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data();
              if (data.reportStatus && Array.isArray(data.reportStatus)) {
                const updatedReportStatus = data.reportStatus.map((status: any) => ({
                  ...status,
                  '월총매출액': monthlyTotal
                }));
                
                await setDoc(dailyReportRef, {
                  ...data,
                  reportStatus: updatedReportStatus,
                  updatedAt: Timestamp.now()
                }, { merge: true });
                
                updatedCount++;
                DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 업데이트] ${date} 업데이트 완료`);
              }
            }
          })
        );
      }
      
      await Promise.all(updatePromises);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[월총매출액 업데이트] ${yearMonth}월 전체 업데이트 완료 (${updatedCount}개 문서 업데이트됨)`);
    } catch (error) {
              DailyReportDebugLogger.error('ERROR_HANDLING', '월총매출액 업데이트 실패:', error);
    }
  };

  // 새로운 월별 집계 저장 함수
  // options.interactive === false: 엑셀 업로드 자동 저장 경로 — 디플레이션 게이트 발화 시
  // 소폭 수축은 저장 진행하되 반환값(deflationNotice)으로 업로드 결과 alert에 경고 병기,
  // 대폭 수축(30% 초과 또는 1천만원 초과)은 자동 경로도 저장 보류 + 사용자 confirm
  // declined === true: 저장을 보류했으므로 호출부는 하류 실제매출/팀성과 새로고침·웹훅 체인을 건너뛴다(방어적).
  const handleSave = async (options?: { interactive?: boolean; yearMonths?: string[] }): Promise<{ deflationNotice?: string; declined?: boolean } | undefined> => {
    const interactive = options?.interactive !== false;
    let deflationNotice: string | undefined;
    try {
      DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
      
      const formattedDate = formatDate(currentDate);

      // 🎯 집계 대상 월은 **호출부가 명시한다**. 화면 상태(currentDate)에서 유추하면 안 된다.
      //    실제로 겪은 결함: 업로드 핸들러는 setCurrentDate(추출한 날짜) 를 부른 뒤 같은 스코프에서
      //    handleSave 를 부르는데, setCurrentDate 는 다음 렌더에 반영되므로 여기서 읽는 currentDate 는
      //    여전히 옛 날짜다. 화면이 9월일 때 8월 파일을 올리면 8월 teamCollection 월누계가 갱신되지
      //    않아, 어시스트가 compositeKey 를 옮긴 달의 금액이 통째로 빠졌다(2026-08 실측 2,723,360원).
      //    파일이 두 달에 걸치는 경우에도 한 달만 집계되던 문제가 함께 해소된다.
      const targetMonths = options?.yearMonths?.length
        ? Array.from(new Set(options.yearMonths)).sort()
        : [formattedDate.substring(0, 7)];

      debugLog('FIREBASE_SAVE', `[SaveReport] 월별 teamCollection 업데이트 시작: ${targetMonths.join(', ')}`);

      // Firebase에서 월별 집계 업데이트 수행
      const { updateMonthlyTeamCollection, analyzeMonthlyReceiptGaps } = await import('../services/firebase/monthlyDataFirebaseService.ts');

      const deflationNotices: string[] = [];

      for (const currentYearMonth of targetMonths) {
        // 먼저 누락 분석 수행
        debugLog('FIREBASE_SAVE', `🔍 [누락 분석] ${currentYearMonth} 월별 수납액 누락 분석 시작...`);
        await analyzeMonthlyReceiptGaps(currentYearMonth);

        let result = await updateMonthlyTeamCollection(currentYearMonth);

        // 🛡️ 디플레이션 게이트: 월누계 총합 급감 감지 시 저장이 보류됨 — 사용자 승인 후에만 재시도
        if (!result.success && result.deflationWarning) {
          const { oldTotal, newTotal } = result.deflationWarning;
          const collapsedDays = result.deflationWarning.collapsedDays || [];

          // 🚫 명세가 지워진 날이 섞여 있으면 승인 경로 자체를 열지 않는다 (정책 결정 2026-09-06).
          //    '총액이 줄어든다'에는 두 가지 원인이 있다 — 명세 소실(사고)과 취소·환불(정상).
          //    앞의 경우 승인은 곧 그 달 총액을 무너뜨리는 일이고, 원본 엑셀이 없으면 되돌릴 수 없다.
          //    실제로 일곱 달 223,197,180원이 '계속할까요?'에 확인을 눌러 지워질 수 있는 상태였다.
          //    두 경로(재계산 버튼·업로드 자동 저장) 모두 같은 규칙을 쓴다.
          if (collapsedDays.length > 0) {
            const blockMessage = buildCollapsedBlockMessage(currentYearMonth, collapsedDays);
            debugWarn('FIREBASE_SAVE', `🚫 [명세 소실 차단] ${currentYearMonth}: ${collapsedDays.map((d) => d.date).join(', ')}`);
            if (interactive) {
              alert(blockMessage);
              return;
            }
            return { declined: true, deflationNotice: blockMessage };
          }

          if (interactive) {
            const proceed = window.confirm(
              `${currentYearMonth} 월누계가 ${oldTotal.toLocaleString()}원 → ${newTotal.toLocaleString()}원으로 크게 줄어듭니다.\n` +
              `(의사/소개 귀속 행 소실 등 원본 데이터 손상 가능성)\n\n계속하시겠습니까?`
            );
            if (!proceed) {
              alert(`${currentYearMonth} 월별 집계 저장이 중단되었습니다.\n일일보고 원본 데이터(teamCollection)를 확인해 주세요.`);
              return;
            }
          } else {
            // 엑셀 업로드 자동 저장 경로: 무음 자동 통과 제거 — 수축 경고를 업로드 결과 alert에 병기하고,
            // 대폭 수축(30% 초과 또는 1천만원 초과)은 자동 경로도 저장 보류 + 사용자 confirm (기준선 파괴·증거 소멸 방지)
            const shrinkAmount = oldTotal - newTotal;
            const shrinkRatio = oldTotal > 0 ? shrinkAmount / oldTotal : 0;
            const isMajorShrink = shrinkRatio > 0.3 || shrinkAmount > 10000000;
            const shrinkSummary = `${oldTotal.toLocaleString()}원 → ${newTotal.toLocaleString()}원 (감소 ${shrinkAmount.toLocaleString()}원${oldTotal > 0 ? `, ${(shrinkRatio * 100).toFixed(1)}%` : ''})`;
            if (isMajorShrink) {
              const proceed = window.confirm(
                `⚠️ [자동 저장 보류] ${currentYearMonth} 월누계가 대폭 줄어듭니다.\n${shrinkSummary}\n` +
                `(의사/소개 귀속 행 소실 등 원본 데이터 손상 가능성)\n\n그래도 월별 집계를 저장하시겠습니까?`
              );
              if (!proceed) {
                return {
                  declined: true,
                  deflationNotice: `⚠️ 월누계 급감 감지로 ${currentYearMonth} 월별 집계 저장을 보류했습니다.\n${shrinkSummary}\n일일보고 원본 데이터(teamCollection)를 확인한 후 수동으로 저장 버튼을 눌러주세요.`
                };
              }
              deflationNotices.push(`⚠️ ${currentYearMonth} 월누계 급감(${shrinkSummary}) 상태에서 사용자 확인 후 월별 집계를 저장했습니다.`);
            } else {
              deflationNotices.push(`⚠️ ${currentYearMonth} 월누계 수축 감지: ${shrinkSummary} — 일일보고 원본 데이터(teamCollection) 확인을 권장합니다.`);
            }
          }
          result = await updateMonthlyTeamCollection(currentYearMonth, { acceptDeflation: true });
        }

        if (!result.success) throw new Error(`${currentYearMonth} 월별 집계 업데이트 실패`);
        debugLog('FIREBASE_SAVE', `[SaveReport] ${currentYearMonth} 월별 집계 업데이트 완료`);
      }

      deflationNotice = deflationNotices.length ? deflationNotices.join('\n') : undefined;

      // 현재 날짜 데이터 다시 로드 — 화면 갱신은 날짜 단위이므로 월 수와 무관하게 한 번만
      const reportData = await getDailyReportData(formattedDate);
      if (reportData) {
        if (reportData.receipts) {
          setReceiptData(reportData.receipts);
        }
        // 하위 호환성: expenses와 financialTransactions 모두 처리
        if (reportData.expenses) {
          setExpenseData(reportData.expenses);
        }

        // 거래 데이터 로드 (하위 호환성 포함) - 엑셀 업로드 중이 아닐 때만 로드
        if (!isExcelUploading) {
          const financialData = await getFinancialTransactionData(formattedDate);
          if (financialData) {
            setFinancialTransactionData(financialData);
          }
        }
        if (reportData.assistReceipts) {
          setAssistReceiptData(reportData.assistReceipts);
        }
        if (reportData.referralReceipts) {
          setReferralReceiptData(reportData.referralReceipts);
        }
      }

      // 🆕 월총매출액 업데이트 (거래 관리 데이터 변경 시에도 반영)
      try {
        await updateMonthlyTotalAfterUpload();
      } catch (monthlyTotalError) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '월총매출액 업데이트 실패:', monthlyTotalError);
        // 월총매출액 업데이트 실패해도 메인 저장은 성공으로 처리
      }

      // 🛡️ 수축 감지 시 경고를 호출부(업로드 결과 alert)로 전달
      return deflationNotice ? { deflationNotice } : undefined;

    } catch (error) {
              DailyReportDebugLogger.error('ERROR_HANDLING', '[SaveReport] 월별 집계 업데이트 중 오류:', error);
      alert(`월별 집계 업데이트 중 오류가 발생했습니다:\n${error.message}`);
    } finally {
      DailyReportDebugLogger.loadingState('END', '위치 7');
        setIsLoading(false);
    }
  };
  
  // Firebase에서 데이터 로드
  useEffect(() => {
    // 🔄 날짜 변경 시 financialTransactionData 상태 강제 초기화 (토요일/일요일 캐시 문제 해결)
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[상태 초기화] 날짜 변경으로 인한 financialTransactionData 초기화:', currentDate);
    setFinancialTransactionData({
      totalExpense: 0,
      totalIncome: 0,
      netAmount: 0,
      items: []
    });

    const loadDailyReportData = async () => {
      try {
        DailyReportDebugLogger.loadingState('START', '파일 업로드 시작');
        setIsLoading(true);
        const formattedDate = formatDate(currentDate);
        const reportData = await getDailyReportData(formattedDate);
        
        if (reportData) {
          if (reportData.receipts) {
            setReceiptData(reportData.receipts);
          }
          
          if (reportData.expenses) {
            setExpenseData(reportData.expenses);
          }
          
          // 새로운 거래 데이터 로드 (하위 호환성 포함) - 엑셀 업로드 중이 아닐 때만 로드
          if (!isExcelUploading) {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[거래 데이터 로드] 시작 - 날짜:', formattedDate);
            const financialData = await getFinancialTransactionData(formattedDate);
            if (financialData) {
              setFinancialTransactionData(financialData);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] ✅ Firebase에서 financialTransactions 로드 성공:', {
                날짜: formattedDate,
                총지출: financialData.totalExpense,
                총수입: financialData.totalIncome,
                항목수: financialData.items?.length || 0
              });
            } else if (reportData.expenses) {
              // expenses 데이터를 financialTransactions 형식으로 변환 (읽기 전용)
              const convertedTransactions: FinancialTransactionData = {
                totalExpense: reportData.expenses.total || 0,
                totalIncome: 0,
                netAmount: -(reportData.expenses.total || 0),
                items: reportData.expenses.items?.map((expense: any) => ({
                  ...expense,
                  transactionType: 'expense' as const,
                  category: '지출'
                })) || []
              };
              setFinancialTransactionData(convertedTransactions);
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] 🔄 expenses → financialTransactions 변환 성공:', {
                날짜: formattedDate,
                원본지출: reportData.expenses.total,
                변환후지출: convertedTransactions.totalExpense,
                항목수: convertedTransactions.items?.length || 0
              });
            } else {
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] ⚠️ 거래 데이터 없음 - 초기값 유지:', formattedDate);
            }
          } else {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[거래 데이터 로드] 엑셀 업로드 중으로 스킵:', formattedDate);
          }
          
          if (reportData.assistReceipts) {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] assistReceipts 데이터:', reportData.assistReceipts);
            
            // 🆕 복합키 시스템 적용: compositeKey가 있는 데이터 처리
            const processedAssistReceipts = reportData.assistReceipts.map(item => {
              if (item.compositeKey) {
                // 복합키에서 원본 어시스트명과 팀명 추출
                const [originalAssistName, teamName] = item.compositeKey.split('-');
                DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[복합키 처리] ${item.compositeKey} → 원본: ${originalAssistName}, 팀: ${teamName}`);
                
                return {
                  ...item,
                  originalAssistName: originalAssistName,
                  teamName: teamName,
                  displayName: `${originalAssistName} (${teamName}팀)` // 화면 표시용
                };
              }
              // 복합키가 없는 기존 데이터는 그대로 유지
              return {
                ...item,
                originalAssistName: item.assistName,
                teamName: '미지정',
                displayName: item.assistName
              };
            });
            
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[복합키 처리 완료] 처리된 assistReceipts:', processedAssistReceipts.length);
            setAssistReceiptData(processedAssistReceipts);
            
            // 🆕 진료의사 정보 유무 확인
            const itemsWithDoctor = processedAssistReceipts.filter(item => item.doctorName);
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', `[데이터 로드] 전체 ${processedAssistReceipts.length}개 중 진료의사 정보 있는 항목: ${itemsWithDoctor.length}개`);
          } else {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] assistReceipts 데이터 없음');
            setAssistReceiptData([]);
          }
          
          // 🆕 assistCashierDoctorReceipts 데이터도 복합키 처리
          if (reportData.assistCashierDoctorReceipts && reportData.assistCashierDoctorReceipts.length > 0) {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] assistCashierDoctorReceipts 데이터:', reportData.assistCashierDoctorReceipts);
            
            // 복합키 처리
//             const processedCashierDoctorData = reportData.assistCashierDoctorReceipts.map(item => {
//               if (item.compositeKey) {
//                 const [originalAssistName, teamName] = item.compositeKey.split('-');
//                 return {
//                   ...item,
//                   originalAssistName: originalAssistName,
//                   teamName: teamName,
//                   displayName: `${originalAssistName} (${teamName}팀)`
//                 };
//               }
//               return {
//                 ...item,
//                 originalAssistName: item.assistName,
//                 teamName: '미지정',
//                 displayName: item.assistName
//               };
//             });
//             
          } else {
            // 🔄 assistCashierDoctorReceipts가 없으면 assistReceipts에서 생성
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] assistCashierDoctorReceipts 없음 - assistReceipts에서 생성');
            if (reportData.assistReceipts && reportData.assistReceipts.length > 0) {
              // assistReceipts 데이터를 이용하여 assistCashierDoctorData 생성 (복합키 처리 포함)
              const generatedCashierDoctorData = reportData.assistReceipts.map((item: any) => {
                const baseItem = {
                  ...item,
                  treatmentType: `${item.cashierName || '미지정수납자'}-${item.doctorName || '미지정의사'}`
                };
                
                if (item.compositeKey) {
                  const [originalAssistName, teamName] = item.compositeKey.split('-');
                  return {
                    ...baseItem,
                    originalAssistName: originalAssistName,
                    teamName: teamName,
                    displayName: `${originalAssistName} (${teamName}팀)`
                  };
                }
                return {
                  ...baseItem,
                  originalAssistName: item.assistName,
                  teamName: '미지정',
                  displayName: item.assistName
                };
              });
              
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] assistReceipts에서 생성된 assistCashierDoctorData:', generatedCashierDoctorData);
            } else {
              DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] assistReceipts도 없음 - 빈 배열 설정');
            }
          }
          
          if (reportData.referralReceipts) {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] referralReceipts 데이터:', reportData.referralReceipts);
            setReferralReceiptData(reportData.referralReceipts);
          } else {
            DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] referralReceipts 데이터 없음');
            setReferralReceiptData([]);
          }
          
          if (reportData.reportStatus) {
            // setReportStatusData(reportData.reportStatus); // 현재 사용하지 않음
          }
          
          // 파일 정보는 ExcelInfo 컴포넌트에서 직접 관리
          
          // 팀별 수납 현황 데이터는 TeamCollectionReport 컴포넌트에서 직접 로드
        } else {
          // reportData가 없는 경우 빈 배열로 초기화
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 로드] reportData 없음 - 빈 데이터로 초기화');
          setReceiptData({ total: 0, items: [] });
          setExpenseData({ total: 0, items: [] });
          // 🔄 financialTransactionData도 명시적으로 초기화 (토요일/일요일 캐시 문제 해결)
          setFinancialTransactionData({
            totalExpense: 0,
            totalIncome: 0,
            netAmount: 0,
            items: []
          });
          setAssistReceiptData([]);
          setReferralReceiptData([]);
          // setReportStatusData([]); // 현재 사용하지 않음
          DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[상태 초기화] 데이터 없음으로 인한 모든 상태 초기화 완료');
        }
      } catch (error) {
        DailyReportDebugLogger.error('ERROR_HANDLING', '일계표 데이터 로드 중 오류 발생:', error);
      } finally {
        DailyReportDebugLogger.loadingState('END', '위치 8');
        setIsLoading(false);
      }
    };
    
    loadDailyReportData();
  }, [currentDate]);
  
  // 현재 날짜에 해당하는 수납 데이터 산출
  const currentDateItems = receiptData.items.filter(item => item.receiptDate === formattedCurrentDate);
  
  // 당일 총수납액 계산 (기존 수납 + 거래 관리 수입)
  const dailyTotalReceipt = React.useMemo(() => {
    const receiptTotal = currentDateItems.reduce((sum, item) => sum + item.amount, 0);
    const financialIncomeTotal = financialTransactionData.totalIncome || 0;
    
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[수입 집계] 데일리 리포트 현황 수입 계산:', {
      receiptTotal: receiptTotal,
      financialIncomeTotal: financialIncomeTotal,
      totalReceipt: receiptTotal + financialIncomeTotal
    });
    
    return receiptTotal + financialIncomeTotal;
  }, [currentDateItems, financialTransactionData]);
  
  // 당일 현금수납 계산 (기존 현금수납 + 거래 관리 수입은 현금으로 간주)
  const dailyCashReceipt = React.useMemo(() => {
    const receiptCashTotal = currentDateItems.reduce((sum, item) => sum + (item.cashAmount || 0), 0);
    const financialIncomeTotal = financialTransactionData.totalIncome || 0;
    
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[현금수납 집계] 현금수납 계산:', {
      receiptCashTotal: receiptCashTotal,
      financialIncomeTotal: financialIncomeTotal,
      totalCashReceipt: receiptCashTotal + financialIncomeTotal
    });
    
    return receiptCashTotal + financialIncomeTotal;
  }, [currentDateItems, financialTransactionData]);
  
  // 당일 카드수납 계산
  const dailyCardReceipt = currentDateItems.reduce((sum, item) => sum + (item.cardAmount || 0), 0);
  
  // 당일 계좌이체 계산
  const dailyTransferReceipt = currentDateItems.reduce((sum, item) => sum + (item.transferAmount || 0), 0);
  
  // 당일 미수금 계산
  const dailyUncollectedReceipt = currentDateItems.reduce((sum, item) => sum + (item.uncollectedAmount || 0), 0);
  
  // 당일 보험금(청구액) 계산
  const dailyInsuranceClaim = currentDateItems.reduce((sum, item) => sum + (item.insuranceClaim || 0), 0);
  
  // 당일 건강생활유지비 계산
  const dailyHealthLifeSupport = currentDateItems.reduce((sum, item) => sum + (item.healthLifeSupport || 0), 0);
    
  // 전일 데이터 계산 - Firebase에서 가져오기
  const [prevDayTotalReceipt, setPrevDayTotalReceipt] = useState(0);
  const [monthTotalReceipt, setMonthTotalReceipt] = useState(0);
  const [monthTotalVAT, setMonthTotalVAT] = useState(0);

        // Firebase에서 전일 총수납액, 월총매출액, 월총부가가치세 가져오기
  useEffect(() => {
    const fetchTotals = async () => {
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[데이터 계산] Firebase에서 가져오기 시작:', formattedCurrentDate);
      
      // 전일 총수납액
      const prevTotal = await getPreviousDayTotal(formattedCurrentDate);
      setPrevDayTotalReceipt(prevTotal);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[전일수납 계산] Firebase 결과:', prevTotal.toLocaleString(), '원');
      
      const currentYearMonth = formattedCurrentDate.substring(0, 7);
      
      // 월총매출액과 월총부가가치세를 병렬로 계산
      debugLog('VAT_CALCULATION', `🧮 [VAT 호출 디버깅] ${currentYearMonth} 월별 데이터 계산 시작...`);
      const [monthTotal, monthVAT] = await Promise.all([
        calculateMonthlyTotal(currentYearMonth),
        calculateMonthlyVAT(currentYearMonth)
      ]);
      
      debugLog('VAT_CALCULATION', `🧮 [VAT 호출 디버깅] ${currentYearMonth} 계산 완료 - 월총매출액: ${monthTotal.toLocaleString()}원, 월 총 VAT: ${monthVAT.toLocaleString()}원`);
      
      setMonthTotalReceipt(monthTotal);
      setMonthTotalVAT(monthVAT);
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[월총매출액 계산] Firebase 결과:', monthTotal.toLocaleString(), '원');
      DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[월총부가가치세 계산] Firebase 결과:', monthVAT.toLocaleString(), '원');
    };
    fetchTotals();
  }, [formattedCurrentDate]);
    
      // Excel 업로드 후 월총매출액 및 월총부가가치세 업데이트
  const updateMonthlyTotalAfterUpload = async () => {
    const currentYearMonth = formattedCurrentDate.substring(0, 7);
    
    DailyReportDebugLogger.loadingState('START', 'updateMonthlyTotalAfterUpload 시작', '월총매출액 및 VAT 업데이트');
    
    // 월총매출액과 월총부가가치세를 병렬로 업데이트
    debugLog('VAT_CALCULATION', `🧮 [Excel 업로드 후 VAT 디버깅] ${currentYearMonth} 업데이트 시작...`);
    const [monthTotal, monthVAT] = await Promise.all([
      calculateMonthlyTotal(currentYearMonth),
      calculateMonthlyVAT(currentYearMonth)
    ]);
    
    debugLog('VAT_CALCULATION', `🧮 [Excel 업로드 후 VAT 디버깅] ${currentYearMonth} 업데이트 완료 - 월총매출액: ${monthTotal.toLocaleString()}원, 월 총 VAT: ${monthVAT.toLocaleString()}원`);
    
    setMonthTotalReceipt(monthTotal);
    setMonthTotalVAT(monthVAT);
    debugLog('EXCEL_UPLOAD', '[Excel 업로드 후] 월총매출액 업데이트:', monthTotal.toLocaleString(), '원');
    debugLog('EXCEL_UPLOAD', '[Excel 업로드 후] 월총부가가치세 업데이트:', monthVAT.toLocaleString(), '원');
  };
    
    DailyReportDebugLogger.loadingState('END', 'updateMonthlyTotalAfterUpload 완료', '월총매출액 및 VAT 업데이트 완료');
  
  // 당일 지출 합계 계산 (기존 지출 + 거래 관리 지출)
  const dailyExpenseTotal = React.useMemo(() => {
    const expenseTotal = expenseData.total || 0;
    const financialExpenseTotal = financialTransactionData.totalExpense || 0;
    
    DailyReportDebugLogger.log('MONTHLY_CALCULATION', '[지출 집계] 데일리 리포트 현황 지출 계산:', {
      expenseDataTotal: expenseTotal,
      financialExpenseTotal: financialExpenseTotal,
      totalExpense: expenseTotal + financialExpenseTotal
    });
    
    return expenseTotal + financialExpenseTotal;
  }, [expenseData.total, financialTransactionData]);
  
  // prevDayTotalReceipt는 위에서 이미 계산됨

  // 기존 데이터에 부가가치세 정보 추가하는 함수
  const addVATToExistingData = async (yearMonth: string) => {
    try {
      debugLog('VAT_CALCULATION', `🧮 [VAT 복구] ====== ${yearMonth} 기존 데이터에 부가가치세 추가 시작 ======`);
      
      const year = parseInt(yearMonth.substring(0, 4));
      const month = parseInt(yearMonth.substring(5, 7));
      const lastDay = new Date(year, month, 0).getDate();
      
      let updatedCount = 0;
      let totalVATAdded = 0;
      
      for (let day = 1; day <= lastDay; day++) {
        const date = `${yearMonth}-${String(day).padStart(2, '0')}`;
        
        try {
          const reportData = await getDailyReportData(date);
          if (reportData && reportData.receipts && reportData.receipts.items) {
            // receipts.items에서 부가가치세 계산
            let dailyVAT = 0;
            reportData.receipts.items.forEach((item: any) => {
              const vat = item.valueAdded || item.부가가치세 || item.VAT || item.부가세 || 0;
              dailyVAT += vat;
            });
            
            debugLog('VAT_CALCULATION', `🧮 [VAT 복구] ${date}: 계산된 VAT = ${dailyVAT.toLocaleString()}원`);
            
            if (dailyVAT > 0) {
              // reportStatus에 부가가치세 정보 추가
              if (reportData.reportStatus && Array.isArray(reportData.reportStatus) && reportData.reportStatus.length > 0) {
                reportData.reportStatus[0]['월총부가가치세'] = dailyVAT;
                reportData.reportStatus[0]['부가가치세'] = dailyVAT;
                reportData.reportStatus[0]['VAT'] = dailyVAT;
                reportData.reportStatus[0]['부가세'] = dailyVAT;
                
                // 업데이트된 데이터 저장
                await saveDailyReportData(reportData, date);
                updatedCount++;
                totalVATAdded += dailyVAT;
                
                debugLog('VAT_CALCULATION', `✅ [VAT 복구] ${date}: ${dailyVAT.toLocaleString()}원 추가됨`);
              }
            }
          }
        } catch (error) {
          debugLog('VAT_CALCULATION', `⚠️ [VAT 복구] ${date}: 데이터 없음 또는 오류`);
          continue;
        }
      }
      
      debugLog('VAT_CALCULATION', `🧮 [VAT 복구] ====== ${yearMonth} 완료 ======`);
      debugLog('VAT_CALCULATION', `✅ 업데이트된 문서: ${updatedCount}개`);
      debugLog('VAT_CALCULATION', `💰 총 추가된 VAT: ${totalVATAdded.toLocaleString()}원`);
      
      // 완료 후 페이지를 새로고침하여 업데이트된 데이터 반영
      if (updatedCount > 0) {
        debugLog('VAT_CALCULATION', '🔄 페이지를 새로고침하여 업데이트된 데이터를 반영합니다...');
        setTimeout(() => window.location.reload(), 2000);
      }
      
    } catch (error) {
      DailyReportDebugLogger.error('ERROR_HANDLING', '❌ [VAT 복구] 오류 발생:', error);
    }
  };

  // 개발자 도구: 전역 함수로 등록 (개발 모드에서만)
  React.useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // 전역 함수로 등록하여 개발자 콘솔에서 호출 가능
      (window as any).cleanupEmptyFiles = async (targetMonth?: string) => {
        const monthToClean = targetMonth || formattedCurrentDate.substring(0, 7);
        debugLog('COMPONENT_LIFECYCLE', `🧹 [개발자 도구] ${monthToClean}월 빈 file 객체 정리를 시작합니다...`);
        await cleanupExistingEmptyFiles(monthToClean);
      };

      // VAT 복구 함수를 전역으로 등록
      (window as any).addVATToExistingData = async (targetMonth?: string) => {
        const monthToProcess = targetMonth || formattedCurrentDate.substring(0, 7);
        await addVATToExistingData(monthToProcess);
      };

      debugLog('COMPONENT_LIFECYCLE', '🛠️ [개발자 도구] 사용 가능한 함수:');
      debugLog('COMPONENT_LIFECYCLE', '• cleanupEmptyFiles("2024-12") - 특정 월의 빈 file 객체 제거');
      debugLog('COMPONENT_LIFECYCLE', '• cleanupEmptyFiles() - 현재 월의 빈 file 객체 제거');
      debugLog('COMPONENT_LIFECYCLE', '🧮 • addVATToExistingData("2025-06") - 특정 월의 기존 데이터에 부가가치세 추가');
      debugLog('COMPONENT_LIFECYCLE', '🧮 • addVATToExistingData() - 현재 월의 기존 데이터에 부가가치세 추가');
    }

    return () => {
      // 컴포넌트 언마운트 시 전역 함수 제거
      if (process.env.NODE_ENV === 'development') {
        delete (window as any).cleanupEmptyFiles;
        delete (window as any).addVATToExistingData;
      }
    };
  }, [formattedCurrentDate]);
  
  return (
    <div className="w-full mx-auto p-4 font-sans">
      {collapsedDaysOfMonth.length > 0 && (
        <div className="mb-4 rounded-lg border border-orange-500 bg-orange-50 px-5 py-4">
          <p className="text-sm font-semibold text-orange-800">
            ⚠️ 이 달은 명세 {collapsedDaysOfMonth.length}일치가 비어 있습니다
          </p>
          <p className="mt-1 text-sm text-orange-800">
            {collapsedDaysOfMonth.map((d) => d.date.slice(5)).join(', ')} — 전표가 없고 금액이 0원입니다.
          </p>
          <p className="mt-2 text-sm text-orange-800">
            월 총액은 보존돼 있지만 <span className="font-semibold">월별 집계 재계산은 차단</span>됩니다.
            그 달 진료비통계 엑셀을 다시 올리면 명세가 되살아납니다.
          </p>
        </div>
      )}
      <DailyReportHeader 
        date={formattedCurrentDate}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onDateChange={handleDateChange}
        isLoading={isLoading || isOptimizing}
        hasUnsavedChanges={false} // TODO: 실제 변경사항 감지 로직 구현
      />
      
      {isLoading ? (
        <div className="text-center py-10">
          <div className="flex justify-center mb-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-700"></div>
          </div>
          <p className="text-gray-600">데이터를 불러오는 중입니다...</p>
          <p className="text-gray-500 text-sm mt-2">잠시만 기다려주세요. 처리 중인 작업이 완료되면 자동으로 페이지가 갱신됩니다.</p>
        </div>
      ) : (
        <>
          <ReceiptInput
            receiptData={receiptData}
            validationErrors={validationErrors}
            currentDate={formattedCurrentDate}
          />

          {/* 어시스트가 빈 수납건의 팀 지정 — 관리자만 편집, 지정은 즉시 저장, 재계산은 '실적 반영' 버튼 */}
          <BlankAssistAttributionPanel
            date={formattedCurrentDate}
            receipts={currentDateItems}
            teams={(() => {
              const monthTeams = budgetData?.monthlyData?.[formattedCurrentDate.slice(0, 7)]?.teams;
              const names: string[] = Array.isArray(monthTeams) && monthTeams.length > 0
                ? monthTeams
                : (teams || []).map((t: any) => t.name);
              return Array.from(new Set(names.filter((n: string) => n && n !== '미지정')));
            })()}
            teamObjects={teams || []}
            employees={employees || []}
            budgetData={budgetData}
            setBudgetData={setBudgetData}
          />
          
          <ExpenseInput
            expenseData={expenseData}
            onSave={handleSave}
            currentDate={formattedCurrentDate}
            setExpenseData={setExpenseData}
            receiptData={receiptData}
            employees={employees || []}
            financialTransactionData={financialTransactionData}
            setFinancialTransactionData={setFinancialTransactionData}
          />

          {/* 🆕 월별 거래관리 합계 섹션 */}
          <MonthlyTransactionSummary
            currentDate={formattedCurrentDate}
          />

          <ExcelInfo
            onFileUpload={handleFileUpload}
            onFileDownload={handleFileDownload}
            currentDate={formattedCurrentDate}
            hasData={receiptData.items.length > 0}
            isExporting={isExporting}
          />
          
          <ReportStatus 
            totalReceipt={dailyTotalReceipt}
            prevDayTotal={prevDayTotalReceipt} // 전일 데이터 계산
            monthlyTotal={monthTotalReceipt} // 월별 데이터 계산
            monthlyVAT={monthTotalVAT} // 월총부가가치세
            currentDate={formattedCurrentDate}
            onSave={handleSave}
            dailyCashReceipt={dailyCashReceipt}
            dailyCardReceipt={dailyCardReceipt}
            dailyTransferReceipt={dailyTransferReceipt}
            dailyUncollectedReceipt={dailyUncollectedReceipt}
            dailyInsuranceClaim={dailyInsuranceClaim}
            dailyHealthLifeSupport={dailyHealthLifeSupport}
            dailyExpenseTotal={dailyExpenseTotal}
          />
          
          {/* TeamCollectionReport 컴포넌트가 제거되었습니다 */}
          
          <MonthlyTeamCollectionReport 
            currentDate={formattedCurrentDate}
          />
          
          <MonthlyAssistantReport 
            currentDate={formattedCurrentDate}
          />
          
          <MonthlyReferralPathReport 
            currentDate={formattedCurrentDate}
          />
          
          <MonthlyTreatmentTypeReport 
            currentDate={formattedCurrentDate}
          />
        </>
      )}
    </div>
  );
};

export default DailyReportPage;